import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './BudgetTable.css';
import { useOutletContext } from 'react-router-dom';
import { useBudgetDataCache } from '../../../contexts/BudgetDataCacheContext';
import { useUserPermissions } from '../../../contexts/UserPermissionsContext';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { useGlobalUsers } from '../../../contexts/GlobalUsersContext';
import { UserCurrency } from '../../../types/auth';
import {
  convertToBackendCurrency,
  convertToFrontendCurrency,
  appCurrencyCodeToBudgetCurrency,
  type FrontendCurrency,
} from './utils/currencyConverter';
import { resolveUserAppConfig } from '../../../utils/userAppConfig';
import { BudgetItem, updateBudget } from '../../../api/budget';
import { useBudgetItemUpdates } from '../../../hooks/dataUpdates/useBudgetItemUpdates';
import { useBudgetDataLoader } from '../../../hooks/useBudgetDataLoader';
import { ResourceType } from '../../../api/userPermissions';
import { generateBudgetItemId } from '../../../utils/generateId';
import expandIcon from '../../../assets/images/expand.png';
import { ReactComponent as ReleaseIcon } from '../../../assets/svg/Budget/release.svg';
import { ReactComponent as ContrastIcon } from '../../../assets/svg/Budget/contrast.svg';
import { ReactComponent as AddCategoryIcon } from '../../../assets/svg/Budget/add.svg';
import UserSearch from '../../../components/UserSearch/UserSearch';
import RelatedBudget from '../../../components/RelatedBudget/RelatedBudget';
import ProgressDialog from '../../../components/UI/ProgressDialog/ProgressDialog';
import DropdownMenu, { DropdownMenuItem } from '../../../components/DropdownMenu';
import {
  BudgetUnitInput,
  BUDGET_UNIT1_PRESETS,
  BUDGET_UNIT2_PRESETS,
} from '../../../components/BudgetUnitInput/BudgetUnitInput';
import { translateBudgetUnitPart } from '../../../i18n/budgetUnitParts';
// 预算分类图标 (SVG)
import { ReactComponent as AllbudgetIcon } from '../../../assets/svg/Budget/Tpye/allbudget.svg';
import { ReactComponent as PreproductionIcon } from '../../../assets/svg/Budget/Tpye/preproduction.svg';
import { ReactComponent as LeadsIcon } from '../../../assets/svg/Budget/Tpye/leads.svg';
import { ReactComponent as CastIcon } from '../../../assets/svg/Budget/Tpye/cast.svg';
import { ReactComponent as ExtrasIcon } from '../../../assets/svg/Budget/Tpye/extras.svg';
import { ReactComponent as TeamIcon } from '../../../assets/svg/Budget/Tpye/team.svg';
import { ReactComponent as ArtIcon } from '../../../assets/svg/Budget/Tpye/art.svg';
import { ReactComponent as EquipmentIcon } from '../../../assets/svg/Budget/Tpye/equipment.svg';
import { ReactComponent as TravelAccommodationIcon } from '../../../assets/svg/Budget/Tpye/travel&accommodation.svg';
import { ReactComponent as LifeProductionIcon } from '../../../assets/svg/Budget/Tpye/life&production.svg';
import { ReactComponent as PostproductionIcon } from '../../../assets/svg/Budget/Tpye/postproduction.svg';
import { ReactComponent as InsuranceIcon } from '../../../assets/svg/Budget/Tpye/insurance.svg';
import { ReactComponent as OthersIcon } from '../../../assets/svg/Budget/Tpye/others.svg';

// 添加按钮图标导入
import { ReactComponent as AddIcon } from '../../../assets/svg/Budget/budgetitem_add.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/svg/Budget/budgetitem_delete.svg';
import { ReactComponent as RelatedIcon } from '../../../assets/svg/Budget/budgetitem_related.svg';

// 导入汇率表功能
import {
  convertCurrency,
  sumAmountsInTargetCurrency,
  formatCurrencyAmount
} from './exchangeRates';


interface LayoutContext {
  tabs: Tab[];
  setTabs: React.Dispatch<React.SetStateAction<Tab[]>>;
}

interface Tab {
  id: string;
  title: string;
  active: boolean;
  path: string;
  fixed?: boolean;
}

interface DeleteConfirmProps {
  visible: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  itemCode?: string;
  itemName?: string;
}

// 基础预算项接口
interface BaseBudgetItem {
  budgetItemID: string;
  budgetItemCode: string;
  budgetItemName: string;
  budgetItemBelongBudgetID: string;
  budgetItemParentID: string | null;
  budgetItemAmount: number;
  budgetItemUsedAmount: number;
  budgetItemRemainingAmount: number;
  budgetItemProgressType: string;
  budgetItemCurrency?: string;
  budgetItemUnit1?: string | null;
  budgetItemUnit2?: string | null;
  budgetItemUnitPrice?: number;
  budgetItemQuantity1?: number;
  budgetItemQuantity2?: number;
  budgetItemRelatedID: string | null;
  budgetItemAssigneeID: string | null;
  budgetItemPartners: string[];
  budgetItemRemark: string;
  budgetItemFilmCode: string | null;
  budgetItemCreateTime: string;
  budgetItemUpdateTime: string;
  budgetItemLevel: number;
  budgetItemRole?: string;
  budgetItemOwnerID?: string;
  budgetItemCreatorID?: string;
  budgetItemUnusedAmount?: number;
  budgetItemExceedingAmount?: number;
  budgetItemStatus?: string;
  budgetItemDataStatus?: string;
}

// 本地预算项接口
interface LocalBudgetItem extends BaseBudgetItem {
  budgetItemChildren?: LocalBudgetItem[];
  budgetItemExpanded?: boolean;
  budgetItemChecked?: boolean;
  isRelatedItem?: boolean; // 标记是否为相关预算项
  isRelatedContainer?: boolean; // 标记是否为相关预算容器（虚拟行）
  relatedParentID?: string; // 相关预算项的父级ID
  calculatedRelatedAmount?: number; // 前端计算的相关预算金额
}

// 服务器更新接口
interface ServerUpdateItem extends Omit<Partial<BaseBudgetItem>, 'budgetItemAssigneeID'> {
  budgetItemAssigneeID?: string;
}

// 更新预算项时使用的接口
interface UpdateBudgetItem extends Partial<BaseBudgetItem> {
  budgetItemAmount?: number;
  budgetItemUsedAmount?: number;
  budgetItemRemainingAmount?: number;
}

// interface BudgetResponse extends Omit<BudgetType, 'budgetStorageMode'> {
//   assigneeInfo?: {
//     userNickname: string;
//     userAvatar: string;
//   };
//   budgetOverview?: string;
//   budgetStorageMode?: '本地' | '在线' | '同步';
//   budgetDataStatus?: '正常' | '草稿' | '已删除';
//   budgetCurrency?: string; // 预算主货币
// }

// 添加可编辑单元格组件
const EditableCell: React.FC<{
  value: string | number;
  onChange: (value: string) => void;
  type?: 'text' | 'number';
  disabled?: boolean;
}> = ({ value = '', onChange, type = 'text', disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  
  // 格式化编辑状态下的数字显示
  const formatEditValue = useCallback((val: string | number): string => {
    if (type !== 'number') return val.toString();
    
    const numValue = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(numValue) || numValue === 0) return '';
    
    // 保持最多2位小数，如果是整数则不显示.00
    if (numValue % 1 === 0) {
      return numValue.toString(); // 整数不显示.00
    } else {
      return numValue.toFixed(2).replace(/\.?0+$/, ''); // 最多2位小数，去掉尾随的0
    }
  }, [type]);
  
  const [inputValue, setInputValue] = useState(() => {
    if (value === null || value === undefined) return '';
    return formatEditValue(value);
  });

  useEffect(() => {
    if (value === null || value === undefined) {
      setInputValue('');
    } else {
      setInputValue(formatEditValue(value));
    }
  }, [value, type, formatEditValue]);

  const handleClick = () => {
    if (!disabled) {
      // 进入编辑状态时确保inputValue是格式化后的值
      setInputValue(formatEditValue(value));
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (!disabled) {
      if (type === 'number' && (!inputValue || inputValue.trim() === '')) {
        onChange('0');
        return;
      }
      onChange(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      if (!disabled) {
        if (type === 'number' && (!inputValue || inputValue.trim() === '')) {
          onChange('0');
          return;
        }
        onChange(inputValue);
      }
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setInputValue(value?.toString() || '');
    }
  };

  const formatValue = (val: string | number) => {
    if (type === 'number') {
      const numValue = typeof val === 'string' ? parseFloat(val) : val;
      if (!isNaN(numValue)) {
        // 检查是否是数量字段（数量¹、数量²）
        const isQuantityField = (val as any)?.__isQuantityField;
        if (isQuantityField) {
          return numValue.toLocaleString('en-HK', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
            useGrouping: true
          });
        }
        return numValue.toLocaleString('en-HK', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true
        });
      }
    }
    return val || '';
  };

  if (isEditing && !disabled) {
    return (
      <div className="editable-cell-wrapper">
        <input
          type={type}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          onFocus={(e) => e.target.select()}
          className="editable-cell-input"
        />
      </div>
    );
  }

  return (
    <div className="editable-cell-wrapper">
      <div 
        className={`editable-cell-content ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
      >
        {formatValue(value)}
      </div>
    </div>
  );
};

// 预算代码可编辑单元格组件（拆分显示：前缀只读 + 后缀可编辑）
interface BudgetCodeEditableCellProps {
  value: string; // 完整预算代码，如 "010104"
  parentCode: string | null; // 父级代码，如 "0101"，如果为 null 表示顶级项
  onChange: (value: string) => void;
  disabled?: boolean;
}

const BudgetCodeEditableCell: React.FC<BudgetCodeEditableCellProps> = ({
  value = '',
  parentCode,
  onChange,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // 提取后缀（最后2位数字）
  const getSuffix = (fullCode: string, prefix: string): string => {
    if (!fullCode || !prefix || fullCode.length <= prefix.length) {
      return '';
    }
    return fullCode.slice(prefix.length);
  };
  
  // 计算初始后缀值（即使 parentCode 为空也要计算，避免 hooks 条件调用）
  const initialSuffix = parentCode ? getSuffix(value, parentCode) : '';
  const [suffixValue, setSuffixValue] = useState(initialSuffix);
  
  // 当 value 或 parentCode 变化时更新 suffixValue
  useEffect(() => {
    if (parentCode) {
      setSuffixValue(getSuffix(value, parentCode));
    }
  }, [value, parentCode]);
  
  // 如果没有父级代码（顶级项），使用原有的 EditableCell
  if (!parentCode || parentCode === '') {
    return (
      <EditableCell
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  
  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
      // 延迟聚焦，确保 DOM 已更新
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  };
  
  const handleSuffixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // 只允许数字，最多2位
    if (newValue === '' || /^\d{0,2}$/.test(newValue)) {
      setSuffixValue(newValue);
    }
  };
  
  const handleBlur = () => {
    setIsEditing(false);
    if (!disabled) {
      // 如果后缀为空，保持原值
      if (suffixValue === '') {
        setSuffixValue(getSuffix(value, parentCode));
        return;
      }
      // 补全为2位数字
      const paddedSuffix = suffixValue.padStart(2, '0');
      const newFullCode = `${parentCode}${paddedSuffix}`;
      onChange(newFullCode);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      if (!disabled) {
        if (suffixValue === '') {
          setSuffixValue(getSuffix(value, parentCode));
          return;
        }
        const paddedSuffix = suffixValue.padStart(2, '0');
        const newFullCode = `${parentCode}${paddedSuffix}`;
        onChange(newFullCode);
      }
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setSuffixValue(getSuffix(value, parentCode));
    }
  };
  
  const displaySuffix = suffixValue || getSuffix(value, parentCode);
  
  return (
    <div className="editable-cell-wrapper">
      <div 
        className={`budget-code-editor ${isEditing ? 'editing' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
      >
        <span className="budget-code-prefix">{parentCode}</span>
        {isEditing && !disabled ? (
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={suffixValue}
            onChange={handleSuffixChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="budget-code-suffix-input"
            maxLength={2}
            pattern="[0-9]*"
          />
        ) : (
          <span className="budget-code-suffix">{displaySuffix}</span>
        )}
      </div>
    </div>
  );
};

const DeleteConfirm: React.FC<DeleteConfirmProps> = ({
  visible,
  onConfirm,
  onCancel,
  title,
  itemCode,
  itemName
}) => {
  const { t } = useTranslation();
  if (!visible) return null;

  return (
    <div className="delete-overlay">
      <div className="delete-content">
        <div className="delete-header">{t('budget.deleteConfirmTitle')}</div>
        <div className="delete-text">
          {t('budget.deleteConfirmMessage')}
          {(() => {
            const code = itemCode?.trim() || '';
            const name = itemName?.trim() || '';
            if (code && name) {
              return `" ${code}  ${name} "`;
            } else if (code) {
              return `" ${code} "`;
            } else if (name) {
              return `" ${name} "`;
            }
            return '';
          })()}
          {t('budget.deleteConfirmSuffix')}
        </div>
        <div className="delete-actions">
          <button 
            className="delete-cancel-button" 
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
          <button 
            className="delete-submit-button" 
            onClick={onConfirm}
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// 在文件顶部添加全局缓存


// 添加货币选项常量
const CURRENCY_OPTIONS = ['RMB', 'HK$', 'US$', 'EUR', 'JPY', 'GBP', 'CAD', 'AUD'] as const;

// 在文件顶部添加标签数据常量
const BUDGET_CATEGORY_IDS = [
  { id: 'allbudget', key: 'filterAll', Icon: AllbudgetIcon, code: null },
  { id: 'preproduction', key: 'filter01', Icon: PreproductionIcon, code: '01' },
  { id: 'leads', key: 'filter02', Icon: LeadsIcon, code: '02' },
  { id: 'cast', key: 'filter03', Icon: CastIcon, code: '03' },
  { id: 'extras', key: 'filter04', Icon: ExtrasIcon, code: '04' },
  { id: 'team', key: 'filter05', Icon: TeamIcon, code: '05' },
  { id: 'art', key: 'filter06', Icon: ArtIcon, code: '06' },
  { id: 'equipment', key: 'filter07', Icon: EquipmentIcon, code: '07' },
  { id: 'travel&accommodation', key: 'filter08', Icon: TravelAccommodationIcon, code: '08' },
  { id: 'life&production', key: 'filter09', Icon: LifeProductionIcon, code: '09' },
  { id: 'postproduction', key: 'filter10', Icon: PostproductionIcon, code: '10' },
  { id: 'insurance', key: 'filter11', Icon: InsuranceIcon, code: '11' },
  { id: 'others', key: 'filter12', Icon: OthersIcon, code: '12' }
] as const;

// 添加货币选择组件
const CurrencySelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // 将货币选项转换为 DropdownMenuItem 格式
  const currencyItems: DropdownMenuItem[] = CURRENCY_OPTIONS.map(currency => ({
    id: currency,
    label: currency,
  }));

  // 计算菜单位置
  useEffect(() => {
    if (menuVisible && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [menuVisible]);

  const handleSelect = (item: DropdownMenuItem) => {
    onChange(item.id as string);
    setMenuVisible(false);
  };

  return (
    <>
      <div
        ref={buttonRef}
        className={`progress-type-select custom-dropdown ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setMenuVisible(!menuVisible)}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
        }}
      >
        {value || 'RMB'}
      </div>
      <DropdownMenu
        items={currencyItems}
        visible={menuVisible && !disabled}
        onClose={() => setMenuVisible(false)}
        onSelect={handleSelect}
        position={menuPosition}
        useFixedPosition={true}
        selectedValue={value}
        width={buttonRef.current?.offsetWidth}
      />
    </>
  );
};

// 添加数量编辑单元格组件
const QuantityEditableCell: React.FC<{
  value: string | number;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ value = '', onChange, disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  
  // 格式化编辑状态下的数量显示
  const formatEditValue = (val: string | number): string => {
    const numValue = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(numValue) || numValue === 0) return '';
    
    // 如果是整数，不显示小数位
    if (numValue % 1 === 0) {
      return numValue.toString();
    } else {
      // 如果有小数，去掉尾随的0，但保留必要的小数位
      return numValue.toString();
    }
  };
  
  const [inputValue, setInputValue] = useState(() => {
    if (value === null || value === undefined) return '';
    return formatEditValue(value);
  });

  useEffect(() => {
    if (value === null || value === undefined) {
      setInputValue('');
    } else {
      setInputValue(formatEditValue(value));
    }
  }, [value]);

  const handleClick = () => {
    if (!disabled) {
      // 进入编辑状态时确保inputValue是格式化后的值
      setInputValue(formatEditValue(value));
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (!disabled) {
      if (!inputValue || inputValue.trim() === '') {
        onChange('1'); // 默认值改为1，不带小数点
        return;
      }
      onChange(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      if (!disabled) {
        if (!inputValue || inputValue.trim() === '') {
          onChange('1'); // 默认值改为1，不带小数点
          return;
        }
        onChange(inputValue);
      }
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setInputValue(formatEditValue(value));
    }
  };

  const formatValue = (val: string | number) => {
    const numValue = typeof val === 'string' ? parseFloat(val) : val;
    if (!isNaN(numValue)) {
      // 如果是整数，不显示小数位
      if (numValue % 1 === 0) {
        return numValue.toLocaleString('en-HK', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          useGrouping: true
        });
      } else {
        // 如果有小数，显示必要的小数位（最多3位）
        return numValue.toLocaleString('en-HK', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 3,
          useGrouping: true
        });
      }
    }
    return val || '';
  };

  if (isEditing && !disabled) {
    return (
      <div className="editable-cell-wrapper">
        <input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          onFocus={(e) => e.target.select()}
          className="editable-cell-input"
          step="0.1"
        />
      </div>
    );
  }

  return (
    <div className="editable-cell-wrapper">
      <div 
        className={`editable-cell-content ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
      >
        {formatValue(value)}
      </div>
    </div>
  );
};

interface BudgetProps {
  budgetId: string;
}

const BudgetTable: React.FC<BudgetProps> = ({ budgetId }) => {
  const { t } = useTranslation();
  const { setTabs } = useOutletContext<LayoutContext>();
  const { getCache, setCache, hasCache, clearCache, subscribeBudgetCache } = useBudgetDataCache();
  const { clearPermissions } = useUserPermissions();
  const { setServerSyncedField, getBehaviorData, getUserInfo } = useCurrentUser();
  const { createBudgetItemData, updateBudgetItemData, deleteBudgetItemData, updateBudgetItemsData } = useBudgetItemUpdates();
  
  // 使用 useBudgetDataLoader 加载预算数据
  const {
    budgetName: loadedBudgetName,
    budgetID: loadedBudgetID,
    totalAmount: loadedTotalAmount,
    budgetItems: loadedBudgetItems,
    budgetStatus: loadedBudgetStatus,
    budgetMainCurrency: loadedBudgetMainCurrency,
    loading: dataLoading,
    error: dataError,
    refresh: refreshBudgetData,
  } = useBudgetDataLoader({
    budgetId: budgetId || '',
    forceRefresh: false,
  });
  const [budgetName, setBudgetName] = useState('未命名');
  const [budgetID, setBudgetID] = useState<string | null>(() => budgetId || null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [budgetItems, setBudgetItems] = useState<LocalBudgetItem[]>([]);

  // 监听 budgetId prop 变化，更新 budgetID state
  useEffect(() => {
    if (budgetId) {
      setBudgetID(budgetId);
    }
  }, [budgetId]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(true);
  const [activeUserSearchId, setActiveUserSearchId] = useState<string | null>(null);
  const [activeRelatedBudgetId, setActiveRelatedBudgetId] = useState<string | null>(null);
  const [, setLoading] = useState(false);
  
  // 添加预算状态管理
  const [budgetStatus, setBudgetStatus] = useState<string>('预算中');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // 添加标签状态管理
  const [activeCategory, setActiveCategory] = useState<string>('allbudget');
  
  // 添加预算总金额货币选择状态
  const [totalAmountCurrency, setTotalAmountCurrency] = useState<string>('RMB');
  // 预算主货币状态（从服务器获取的预算级别货币）
  const [budgetMainCurrency, setBudgetMainCurrency] = useState<string>('RMB');

  // 进度对话框状态管理
  const [progressDialogVisible, setProgressDialogVisible] = useState<boolean>(false);
  const [progressDialogMessage, setProgressDialogMessage] = useState<string>('');
  const [progressDialogProgress, setProgressDialogProgress] = useState<number>(0);

  // 处理主货币变更
  const handleMainCurrencyChange = async (newCurrency: string) => {
    const oldCurrency = totalAmountCurrency;
    if (oldCurrency === newCurrency) return;

    console.log(`💱 主货币变更:`, { oldCurrency, newCurrency });
    
    try {
      // 更新显示货币
      setTotalAmountCurrency(newCurrency);
      
      // 查找所有与旧货币相同的原始预算项（排除虚拟相关预算项）
      const findSameCurrencyItems = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
        const result: LocalBudgetItem[] = [];
        const processItems = (currentItems: LocalBudgetItem[]) => {
          currentItems.forEach(item => {
            // 排除虚拟相关预算项和相关预算容器
            if (!item.isRelatedItem && !item.isRelatedContainer) {
              // 检查是否为相同货币（如果货币字段为空，默认为主货币）
              const itemCurrency = item.budgetItemCurrency || oldCurrency;
              if (itemCurrency === oldCurrency) {
                result.push(item);
              }
            }
            // 递归处理子项
            if (item.budgetItemChildren) {
              processItems(item.budgetItemChildren);
            }
          });
        };
        processItems(items);
        return result;
      };

      const sameCurrencyItems = findSameCurrencyItems(budgetItems);
      
      if (sameCurrencyItems.length > 0) {
        console.log(`🔄 找到 ${sameCurrencyItems.length} 个需要货币转换的预算项`, sameCurrencyItems.map(item => item.budgetItemCode));
        
        // 显示进度对话框
        setProgressDialogVisible(true);
        setProgressDialogMessage(t('budget.currencyChangeProgress'));
        setProgressDialogProgress(0);
        
        // 准备批量更新数据
        const batchUpdateData = sameCurrencyItems.map(item => {
          // 进行货币汇率换算
          const convertedAmount = convertCurrency(item.budgetItemAmount, oldCurrency, newCurrency);
          const convertedUsedAmount = convertCurrency(item.budgetItemUsedAmount, oldCurrency, newCurrency);
          const convertedUnitPrice = item.budgetItemUnitPrice ? 
            convertCurrency(item.budgetItemUnitPrice, oldCurrency, newCurrency) : undefined;

          return {
            budgetItemID: item.budgetItemID,
            data: {
              budgetItemCurrency: newCurrency,
              budgetItemAmount: convertedAmount,
              budgetItemUsedAmount: convertedUsedAmount,
              budgetItemRemainingAmount: convertedAmount - convertedUsedAmount,
              ...(convertedUnitPrice !== undefined && { budgetItemUnitPrice: convertedUnitPrice })
            }
          };
        });

        // 分批处理大量更新，避免请求过大
        const BATCH_SIZE = 30; // 每批最多处理20个预算项
        const batches = [];
        for (let i = 0; i < batchUpdateData.length; i += BATCH_SIZE) {
          batches.push(batchUpdateData.slice(i, i + BATCH_SIZE));
        }

        console.log(`📦 准备分 ${batches.length} 批次更新预算项`);

        // 按预算代码顺序分批更新
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          console.log(`🚀 正在更新第 ${i + 1}/${batches.length} 批次，包含 ${batch.length} 个预算项`);
          
          try {
            // 使用统一更新 hook 批量更新
            await updateBudgetItemsData(budgetID!, batch);
            console.log(`✅ 第 ${i + 1} 批次更新成功`);
            
            // 更新进度
            const progress = Math.round(((i + 1) / batches.length) * 80); // 批量更新占80%的进度
            setProgressDialogProgress(progress);
            
            // 批次间稍作延迟，避免服务器压力
            if (i < batches.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (batchError) {
            console.error(`❌ 第 ${i + 1} 批次更新失败:`, batchError);
            throw batchError;
          }
        }

        // 更新本地状态
        const updateLocalItems = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
          return items.map(item => {
            if (!item.isRelatedItem && !item.isRelatedContainer) {
              const itemCurrency = item.budgetItemCurrency || oldCurrency;
              if (itemCurrency === oldCurrency) {
                // 进行货币汇率换算
                const convertedAmount = convertCurrency(item.budgetItemAmount, oldCurrency, newCurrency);
                const convertedUsedAmount = convertCurrency(item.budgetItemUsedAmount, oldCurrency, newCurrency);
                const convertedUnitPrice = item.budgetItemUnitPrice ? 
                  convertCurrency(item.budgetItemUnitPrice, oldCurrency, newCurrency) : item.budgetItemUnitPrice;

                const updatedItem = {
                  ...item,
                  budgetItemCurrency: newCurrency,
                  budgetItemAmount: convertedAmount,
                  budgetItemUsedAmount: convertedUsedAmount,
                  budgetItemRemainingAmount: convertedAmount - convertedUsedAmount,
                  ...(convertedUnitPrice !== undefined && { budgetItemUnitPrice: convertedUnitPrice })
                };

                return {
                  ...updatedItem,
                  budgetItemChildren: updatedItem.budgetItemChildren ? updateLocalItems(updatedItem.budgetItemChildren) : updatedItem.budgetItemChildren
                };
              }
            }
            
            return {
              ...item,
              budgetItemChildren: item.budgetItemChildren ? updateLocalItems(item.budgetItemChildren) : item.budgetItemChildren
            };
          });
        };

        const updatedBudgetItems = updateLocalItems(budgetItems);
        setBudgetItems(updatedBudgetItems);
        
        // 更新进度到90%
        setProgressDialogProgress(90);
        
        console.log(`✅ ${sameCurrencyItems.length} 个预算项货币转换完成`);
        
        // 重新构建树结构以同步相关预算项
        setTimeout(async () => {
          console.log('🔄 同步相关预算项...');
          setProgressDialogProgress(95);
          // 刷新预算数据（从缓存或API）
          if (budgetId) {
            await refreshBudgetData();
          }
          setProgressDialogProgress(100);
          
          // 完成后隐藏进度对话框
          setTimeout(() => {
            setProgressDialogVisible(false);
          }, 500);
        }, 100);
      }
      
      // 如果有预算ID，更新服务器上的预算主货币
      if (budgetID) {
        const updateData = {
          budgetCurrency: newCurrency,
          budgetTotalAmount: calculateTotalAmount(budgetItems, newCurrency)
        };
        
        console.log(`🚀 更新预算主货币到服务器:`, updateData);
        
        const response = await updateBudget(budgetID, updateData);
        
        if (response.data) {
          console.log(`✅ 预算主货币更新成功:`, response.data);
          setBudgetMainCurrency(newCurrency);
          setTotalAmount(updateData.budgetTotalAmount);
        }
      }
      
    } catch (error) {
      console.error('❌ 更新预算主货币失败:', error);
      // 隐藏进度对话框
      setProgressDialogVisible(false);
      // 回滚货币变更
      setTotalAmountCurrency(oldCurrency);
      // 重新加载预算项以恢复状态
      if (budgetId) {
        await refreshBudgetData();
      }
    }
  };

  // 筛选预算项的函数
  const filterBudgetItems = (items: LocalBudgetItem[], categoryCode: string | null): LocalBudgetItem[] => {
    if (!categoryCode) {
      // 如果选择"预算总览"，显示所有项目
      return items;
    }

    // 直接查找匹配的预算项
    const matchedItems = items.filter(item => item.budgetItemCode === categoryCode);
    
    // 返回匹配的项目及其所有子项
    return matchedItems.map(item => ({
      ...item,
      budgetItemChildren: item.budgetItemChildren || []
    }));
  };

  // 获取当前显示的预算项
  const getDisplayedBudgetItems = (): LocalBudgetItem[] => {
    const activeCategoryData = BUDGET_CATEGORY_IDS.find(cat => cat.id === activeCategory);
    return filterBudgetItems(budgetItems, activeCategoryData?.code || null);
  };

  // 检查是否应该隐藏新建第1级预算项按钮
  const shouldHideAddTopItem = (): boolean => {
    return activeCategory !== 'allbudget';
  };

  // isNewTab 变量已移除，现在由 useBudgetDataLoader 处理数据加载

  // getExpandedState 函数已移除，现在由 useBudgetDataLoader 处理数据加载
  // 展开状态通过 budgetItemExpanded 属性管理

  // 保存展开状态到 localStorage
  const saveExpandedState = useCallback((items: LocalBudgetItem[]) => {
    if (!budgetId) return;
    const states: Record<string, boolean> = {};
    const saveStates = (items: LocalBudgetItem[]) => {
      items.forEach(item => {
        states[item.budgetItemID] = !!item.budgetItemExpanded;
        if (item.budgetItemChildren) {
          saveStates(item.budgetItemChildren);
        }
      });
    };
    saveStates(items);
    localStorage.setItem(`budgetExpandStates_${budgetId}`, JSON.stringify(states));
  }, [budgetId]);

  // 监听 budgetItems 变化，保存展开状态
  useEffect(() => {
    if (budgetItems.length > 0) {
      saveExpandedState(budgetItems);
    }
  }, [budgetItems, budgetId, saveExpandedState]);

  // 组件卸载时保存展开状态
  useEffect(() => {
    return () => {
      if (budgetItems.length > 0) {
        saveExpandedState(budgetItems);
      }
    };
  }, [budgetItems, saveExpandedState]);

  // 处理展开/收起
  const toggleExpand = (itemId: string) => {
    setBudgetItems(items => {
      const updateItem = (item: LocalBudgetItem): LocalBudgetItem => {
        if (item.budgetItemID === itemId) {
          return {
            ...item,
            budgetItemExpanded: !item.budgetItemExpanded
          };
        }
        if (item.budgetItemChildren) {
          return { ...item, budgetItemChildren: item.budgetItemChildren.map(updateItem) };
        }
        return item;
      };
      const updatedItems = items.map(updateItem);
      return updatedItems;
    });
  };

  // 修改 updateBudgetItem 函数 - 使用统一更新 hook
  const updateBudgetItem = useCallback(async (itemId: string, updatedItem: UpdateBudgetItem, isNewItem: boolean = false) => {
    try {
      // 转换为服务器接受的格式
      const serverItem: ServerUpdateItem = {
        ...updatedItem,
        budgetItemAssigneeID: updatedItem.budgetItemAssigneeID === null ? undefined : updatedItem.budgetItemAssigneeID,
        budgetItemRelatedID: updatedItem.budgetItemRelatedID === null ? undefined : updatedItem.budgetItemRelatedID
      };

      // 如果是新建项目，使用统一更新 hook
      if (isNewItem) {
        const budgetAmount = updatedItem.budgetItemAmount || 0;
        const usedAmount = updatedItem.budgetItemUsedAmount || 0;
        const unusedAmount = budgetAmount - usedAmount;
        
        // 从 serverItem 中排除 BudgetItem API 不支持的字段
        const { budgetItemPartners, budgetItemRemainingAmount, ...serverItemWithoutPartners } = serverItem;
        
        const newItem: Partial<BudgetItem> = {
          ...serverItemWithoutPartners,
          budgetItemID: itemId,
          budgetItemBelongBudgetID: budgetID!,
          budgetItemCode: updatedItem.budgetItemCode || '',
          budgetItemName: updatedItem.budgetItemName || '',
          budgetItemParentID: updatedItem.budgetItemParentID || null,
          budgetItemAmount: budgetAmount,
          budgetItemUsedAmount: usedAmount,
          budgetItemUnusedAmount: unusedAmount,
          budgetItemExceedingAmount: updatedItem.budgetItemExceedingAmount || 0,
          budgetItemProgressType: updatedItem.budgetItemProgressType || '按时间进度',
          budgetItemRemark: updatedItem.budgetItemRemark || '',
          budgetItemFilmCode: updatedItem.budgetItemFilmCode || null,
          budgetItemStatus: updatedItem.budgetItemStatus || '预算中',
          budgetItemDataStatus: updatedItem.budgetItemDataStatus || '正常',
          budgetItemLevel: updatedItem.budgetItemLevel || 0,
          budgetItemRelatedID: updatedItem.budgetItemRelatedID === null ? undefined : updatedItem.budgetItemRelatedID
        };

        // 使用统一更新 hook 创建预算项
        const createdItem = await createBudgetItemData(budgetID!, newItem);
        return createdItem;
      }

      // 如果是更新项目，使用统一更新 hook
      // 转换 serverItem 为符合 BudgetItem 类型的格式
      // 排除 BudgetItem API 不支持的字段
      const { budgetItemPartners, budgetItemRemainingAmount, ...serverItemWithoutPartners } = serverItem;
      const updateData: Partial<BudgetItem> = {
        ...serverItemWithoutPartners,
        budgetItemRelatedID: serverItem.budgetItemRelatedID === null ? undefined : serverItem.budgetItemRelatedID
      };
      
      // 使用统一更新 hook 更新预算项
      await updateBudgetItemData(budgetID!, itemId, updateData);
      
      // 返回更新后的数据（从缓存中获取）
      const cache = getCache(budgetID!);
      if (cache) {
        // 在树中查找更新后的项目
        const findItem = (items: any[]): BudgetItem | null => {
          for (const item of items) {
            if (item.budgetItemID === itemId) {
              return item;
            }
            if (item.budgetItemChildren) {
              const found = findItem(item.budgetItemChildren);
              if (found) return found;
            }
          }
          return null;
        };
        return findItem(cache.budgetItems) || updatedItem as BudgetItem;
      }
      
      return updatedItem as BudgetItem;
    } catch (error) {
      console.error('操作预算项时出错:', error);
      throw error;
    }
  }, [budgetID, createBudgetItemData, updateBudgetItemData, getCache]);


  // 存储原始名称，避免不必要的API调用
  const [originalBudgetName, setOriginalBudgetName] = useState<string>('');

  // 处理预算名称失焦
  const handleNameBlur = React.useCallback(async () => {
    try {
      // 检查是否有预算ID
      if (!budgetID) {
        console.error('预算ID不存在，无法更新名称');
        return;
      }

      const trimmedName = budgetName.trim();
      if (!trimmedName) {
        setBudgetName('未命名预算');
        return;
      }

      // 优化：与当前显示的名称比较，避免不必要的API调用
      // 注意：这里应该与当前数据库中的名称比较，而不是初始名称
      if (trimmedName === originalBudgetName.trim()) {
        console.log('预算名称无变化，跳过更新');
        return;
      }

      // 发送更新请求
      await updateBudget(budgetID, { 
        budgetName: trimmedName,
        budgetID
      });

      // 更新原始名称缓存
      setOriginalBudgetName(trimmedName);

      // 修复：始终更新标签页标题，因为Layout层面已经优化了重新渲染问题
      setTabs(prev => prev.map(tab => 
        tab.id === `budget-${budgetID}` 
          ? { ...tab, title: trimmedName }
          : tab
      ));

      console.log('预算名称更新成功:', trimmedName);

    } catch (err: any) {
      console.error('更新预算名称失败:', err);
      // 发生错误时恢复原来的名称
      setBudgetName(originalBudgetName || '未命名预算');
    }
  }, [budgetID, budgetName, originalBudgetName, setTabs]);

  // 处理预算名称按键事件
  const handleNameKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // 失去焦点会自动触发保存
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      // ESC键恢复原值
      setBudgetName(originalBudgetName || '未命名预算');
      (e.target as HTMLInputElement).blur();
    }
  }, [originalBudgetName, setBudgetName]);

  // 处理删除预算项
  const handleDelete = async (itemId: string) => {
    try {
      // 查找要删除的项及其所有子项
      const findItemsToDelete = (items: LocalBudgetItem[]): string[] => {
        const itemsToDelete: string[] = [];
        const findItems = (currentItems: LocalBudgetItem[]) => {
          for (const item of currentItems) {
            if (item.budgetItemID === itemId) {
              itemsToDelete.push(item.budgetItemID);
              if (item.budgetItemChildren) {
                item.budgetItemChildren.forEach(child => {
                  itemsToDelete.push(child.budgetItemID);
                  if (child.budgetItemChildren) {
                    findItems(child.budgetItemChildren);
                  }
                });
              }
              break;
            }
            if (item.budgetItemChildren) {
              findItems(item.budgetItemChildren);
            }
          }
        };
        findItems(items);
        return itemsToDelete;
      };

      const itemsToDelete = findItemsToDelete(budgetItems);
      
      // 使用统一更新 hook 删除预算项
      for (const id of itemsToDelete) {
        await deleteBudgetItemData(budgetID!, id);
      }

      // 从缓存获取最新的预算项数据
      const cache = getCache(budgetID!);
      let finalItems: LocalBudgetItem[] = [];
      
      if (cache && cache.budgetItems) {
        finalItems = cache.budgetItems;
        setBudgetItems(finalItems);
      } else {
        // 如果缓存不存在，刷新数据
        await refreshBudgetData();
        // 从缓存获取更新后的数据
        const updatedCache = getCache(budgetID!);
        if (updatedCache && updatedCache.budgetItems) {
          finalItems = updatedCache.budgetItems;
          setBudgetItems(finalItems);
        } else {
          throw new Error('获取预算项数据失败');
        }
      }

      // 计算并更新总金额
      const newTotalAmount = finalItems
        .reduce((sum: number, item: LocalBudgetItem) => sum + (typeof item.budgetItemAmount === 'string' ? 
          parseFloat(item.budgetItemAmount) || 0 : 
          item.budgetItemAmount || 0), 0);

      // 更新预算总金额
      await updateBudget(budgetID!, { budgetTotalAmount: Number(newTotalAmount) });
      setTotalAmount(newTotalAmount);

      // 关闭确认框
      setDeleteModalVisible(false);
      setDeleteItemId(null);
    } catch (err: any) {
      console.error('删除预算项失败:', err);
      if (err.response?.data?.message) {
        setError(t('budget.deleteFailed') + err.response.data.message);
      } else if (err.response?.data?.error) {
        setError(t('budget.deleteFailed') + err.response.data.error);
      } else if (err.message) {
        setError(t('budget.deleteFailed') + err.message);
      } else {
        setError(t('budget.deleteFailed') + t('common.unknownError'));
      }
    }
  };

  // 计算子项总金额
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateChildrenSum = (items: LocalBudgetItem[]): number => {
    return items.reduce((sum, item) => {
      // 确保金额始终为数字
      const amount = typeof item.budgetItemAmount === 'string' ? 
        parseFloat(item.budgetItemAmount) || 0 : 
        item.budgetItemAmount || 0;
      
      // 如果有子项，递归计算子项的总和
      if (item.budgetItemChildren && item.budgetItemChildren.length > 0) {
        return sum + calculateChildrenSum(item.budgetItemChildren);
      }
      
      // 如果没有子项，直接返回当前项的金额
      return sum + amount;
    }, 0);
  };

  // 更新父级金额（支持汇率换算）
  const updateParentAmounts = useCallback((items: LocalBudgetItem[]): LocalBudgetItem[] => {
    return items.map(item => {
      if (item.budgetItemChildren && item.budgetItemChildren.length > 0) {
        const children = updateParentAmounts(item.budgetItemChildren);
        
        // 分别计算普通子项和相关预算项
        const normalChildren = children.filter(child => !child.isRelatedItem && !child.isRelatedContainer);
        
        // 使用汇率换算计算普通子项的金额总和（转换为父级货币）
        const parentCurrency = item.budgetItemCurrency || 'RMB';
        const childrenAmounts = normalChildren.map(child => ({
          amount: child.budgetItemAmount || 0,
          currency: child.budgetItemCurrency || 'RMB'
        }));
        const childrenUsedAmounts = normalChildren.map(child => ({
          amount: child.budgetItemUsedAmount || 0,
          currency: child.budgetItemCurrency || 'RMB'
        }));
        
        const totalAmount = sumAmountsInTargetCurrency(childrenAmounts, parentCurrency);
        const totalUsedAmount = sumAmountsInTargetCurrency(childrenUsedAmounts, parentCurrency);
        
        // 计算相关预算金额总和（转换为父级货币）
        const calculateRelatedAmountSum = (items: LocalBudgetItem[], targetCurrency: string): number => {
          return items.reduce((sum, item) => {
            // 如果是相关预算项，将其金额转换为目标货币
            if (item.isRelatedItem && item.calculatedRelatedAmount) {
              const convertedAmount = convertCurrency(
                item.calculatedRelatedAmount,
                item.budgetItemCurrency || 'RMB',
                targetCurrency
              );
              return sum + convertedAmount;
            }
            // 如果是普通预算项，递归计算其子项的相关预算金额
            const childrenAmount = item.budgetItemChildren ? 
              calculateRelatedAmountSum(item.budgetItemChildren, targetCurrency) : 0;
            return sum + childrenAmount;
          }, 0);
        };
        
        // 计算所有子项的相关预算金额总和（转换为父级货币）
        const totalRelatedAmount = calculateRelatedAmountSum(children, parentCurrency);
        
        return {
          ...item,
          budgetItemChildren: children,
          budgetItemAmount: totalAmount,
          budgetItemUsedAmount: totalUsedAmount,
          budgetItemRemainingAmount: totalAmount - totalUsedAmount,
          calculatedRelatedAmount: totalRelatedAmount > 0 ? totalRelatedAmount : undefined
        };
      }
      return item;
    });
  }, []);

  // 收集所有预算项ID（包括子项）
  const collectBudgetItemIDs = useCallback((items: LocalBudgetItem[]): string[] => {
    const ids: string[] = [];
    const collect = (items: LocalBudgetItem[]) => {
      items.forEach((item) => {
        ids.push(item.budgetItemID);
        if (item.budgetItemChildren && item.budgetItemChildren.length > 0) {
          collect(item.budgetItemChildren);
        }
      });
    };
    collect(items);
    return ids;
  }, []);

  // 使用 useBudgetDataLoader 加载的数据更新本地状态
  useEffect(() => {
    if (dataLoading) {
      setLoading(true);
      return;
    }

    if (dataError) {
      setError(dataError);
      setLoading(false);
      return;
    }

    // 数据加载完成，更新状态
    if (loadedBudgetName) {
      setBudgetName(loadedBudgetName);
    }
    if (loadedBudgetID) {
      setBudgetID(loadedBudgetID);
    }
    if (loadedTotalAmount !== undefined) {
      setTotalAmount(loadedTotalAmount);
    }
    if (loadedBudgetItems) {
      setBudgetItems(loadedBudgetItems);
    }
    if (loadedBudgetStatus) {
      setBudgetStatus(loadedBudgetStatus);
    }
    if (loadedBudgetMainCurrency) {
      setBudgetMainCurrency(loadedBudgetMainCurrency);
      setTotalAmountCurrency(loadedBudgetMainCurrency);
    }

    setLoading(false);
  }, [dataLoading, dataError, loadedBudgetName, loadedBudgetID, loadedTotalAmount, loadedBudgetItems, loadedBudgetStatus, loadedBudgetMainCurrency]);

  // 从缓存恢复状态
  const restoreFromCache = useCallback((cachedData: any) => {
    if (cachedData.budgetName !== undefined) setBudgetName(cachedData.budgetName);
    if (cachedData.budgetID !== undefined) setBudgetID(cachedData.budgetID);
    if (cachedData.totalAmount !== undefined) setTotalAmount(cachedData.totalAmount);
    if (cachedData.budgetItems) setBudgetItems(cachedData.budgetItems);
    if (cachedData.budgetStatus !== undefined) setBudgetStatus(cachedData.budgetStatus);
    if (cachedData.activeCategory !== undefined) setActiveCategory(cachedData.activeCategory);
    if (cachedData.totalAmountCurrency !== undefined) setTotalAmountCurrency(cachedData.totalAmountCurrency);
    if (cachedData.budgetMainCurrency !== undefined) setBudgetMainCurrency(cachedData.budgetMainCurrency);
    if (cachedData.isAllExpanded !== undefined) setIsAllExpanded(cachedData.isAllExpanded);
  }, []);

  // 组件挂载时初始化（现在主要依赖 useBudgetDataLoader，这里只处理缓存恢复）
  useEffect(() => {
    if (!budgetId) {
      return;
    }

    // 检查缓存（只有在缓存数据完整时才使用）
    if (hasCache(budgetId)) {
      const cachedData = getCache(budgetId);
      if (cachedData) {
        // 验证缓存数据是否有效
        const hasValidBudgetID = cachedData.budgetID && typeof cachedData.budgetID === 'string';
        const hasValidBudgetItems = Array.isArray(cachedData.budgetItems);
        const hasData = hasValidBudgetItems && cachedData.budgetItems.length > 0;
        
        // 只有当缓存包含有效数据时才使用缓存
        if (hasValidBudgetID && hasValidBudgetItems && hasData) {
          console.log('从缓存恢复 Budget 数据', { budgetId, itemCount: cachedData.budgetItems.length });
          restoreFromCache(cachedData);
        } else {
          // 缓存无效或不完整，清除缓存
          console.warn('Budget 缓存数据无效或不完整，清除缓存', { 
            budgetId, 
            hasValidBudgetID, 
            hasValidBudgetItems,
            hasData,
            itemCount: cachedData.budgetItems?.length || 0,
          });
          clearCache(budgetId);
          // 清除相关权限缓存
          clearPermissions(ResourceType.BUDGET, budgetId);
          // 清除所有预算项权限（如果缓存中有预算项数据）
          if (cachedData.budgetItems && Array.isArray(cachedData.budgetItems)) {
            const budgetItemIDs = collectBudgetItemIDs(cachedData.budgetItems);
            budgetItemIDs.forEach((budgetItemID) => {
              clearPermissions(ResourceType.BUDGET_ITEM, budgetItemID);
            });
          }
        }
      }
    }
  }, [budgetId, hasCache, getCache, clearCache, restoreFromCache, clearPermissions, collectBudgetItemIDs]);

  // 订阅缓存变化，自动更新 UI
  useEffect(() => {
    if (!budgetId) return;

    const unsubscribe = subscribeBudgetCache(budgetId, () => {
      // 缓存更新时，从缓存恢复数据
      const cache = getCache(budgetId);
      if (cache) {
        if (cache.budgetItems) {
          setBudgetItems(cache.budgetItems);
        }
        if (cache.totalAmount !== undefined) {
          setTotalAmount(cache.totalAmount);
        }
        if (cache.budgetName !== undefined) {
          setBudgetName(cache.budgetName);
        }
        if (cache.budgetStatus !== undefined) {
          setBudgetStatus(cache.budgetStatus);
        }
        if (cache.totalAmountCurrency !== undefined) {
          setTotalAmountCurrency(cache.totalAmountCurrency);
        }
        if (cache.budgetMainCurrency !== undefined) {
          setBudgetMainCurrency(cache.budgetMainCurrency);
        }
        if (cache.isAllExpanded !== undefined) {
          setIsAllExpanded(cache.isAllExpanded);
        }
      }
    });

    return unsubscribe;
  }, [budgetId, subscribeBudgetCache, getCache]);

  // 保存状态到缓存（当关键状态变化时）
  useEffect(() => {
    if (!budgetId) return;
    
    // 只在有数据时才保存到缓存
    if (budgetItems.length > 0 || budgetID) {
      setCache(budgetId, {
        budgetName,
        budgetID,
        totalAmount,
        budgetItems,
        budgetStatus,
        activeCategory,
        totalAmountCurrency,
        budgetMainCurrency,
        isAllExpanded,
      });
    }
  }, [budgetId, budgetName, budgetID, totalAmount, budgetItems, budgetStatus, activeCategory, totalAmountCurrency, budgetMainCurrency, isAllExpanded, setCache]);

  // 计算预算总金额（支持汇率换算）
  const calculateTotalAmount = (items: LocalBudgetItem[], targetCurrency: string = totalAmountCurrency): number => {
    const topLevelAmounts = items
      .filter(item => !item.budgetItemParentID)
      .map(item => ({
        amount: typeof item.budgetItemAmount === 'string' ? parseFloat(item.budgetItemAmount) : (item.budgetItemAmount || 0),
        currency: item.budgetItemCurrency || 'RMB'
      }));
    
    return sumAmountsInTargetCurrency(topLevelAmounts, targetCurrency);
  };


  // 修改处理单元格变更的函数
  const handleCellChange = async (itemId: string, field: keyof LocalBudgetItem, value: string | number) => {
    
    // 如果是相关预算容器，只允许编辑货币字段
    if (itemId.startsWith('related_container_')) {
      if (field !== 'budgetItemCurrency') {
        console.log('❌ 相关预算容器仅允许编辑货币字段');
        return;
      }
      
      // 处理相关预算容器的货币变更
      console.log('🔄 相关预算容器货币变更:', { itemId, field, value });
      
      setBudgetItems(prevItems => {
        const updateItems = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
          return items.map(item => {
            if (item.budgetItemID === itemId) {
              const oldCurrency = item.budgetItemCurrency || 'RMB';
              const newCurrency = value as string;
              
              // 重新计算相关预算容器的总金额（转换为新货币，但不影响引用行）
              if (oldCurrency !== newCurrency && item.budgetItemChildren) {
                // 计算所有引用行的原始金额，转换为容器的新货币
                const relatedAmountsInNewCurrency = item.budgetItemChildren.map(relatedChild => {
                  if (relatedChild.isRelatedItem) {
                    // 从引用行的ID提取原始预算项ID
                    const originalItemId = relatedChild.budgetItemID.replace('related_', '');
                    const originalItem = findBudgetItem(prevItems, originalItemId);
                    if (originalItem) {
                      return {
                        amount: convertCurrency(
                          originalItem.budgetItemAmount || 0,
                          originalItem.budgetItemCurrency || 'RMB',
                          newCurrency
                        ),
                        currency: newCurrency
                      };
                    }
                  }
                  return { amount: 0, currency: newCurrency };
                });
                
                const totalRelatedAmount = relatedAmountsInNewCurrency.reduce((sum, item) => sum + item.amount, 0);
                
                console.log(`🔄 相关预算容器货币变更，重新计算总金额:`, {
                  containerId: itemId,
                  oldCurrency,
                  newCurrency,
                  totalRelatedAmount
                });
                
                return {
                  ...item,
                  budgetItemCurrency: newCurrency,
                  calculatedRelatedAmount: totalRelatedAmount
                  // 注意：不修改子项，让引用行保持其原始预算项的货币
                };
              }
              
              return {
                ...item,
                budgetItemCurrency: newCurrency
              };
            }
            
            if (item.budgetItemChildren) {
              return {
                ...item,
                budgetItemChildren: updateItems(item.budgetItemChildren)
              };
            }
            
            return item;
          });
        };
        
        return updateItems(prevItems);
      });
      
      return;
    }
    
    // 创建一个新的 Map 来存储所有需要更新的项目
    const itemsToUpdate = new Map<string, UpdateBudgetItem>();
    
    // 如果是相关预算项编辑相关预算金额，需要找到原始预算项
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const findOriginalItem = (items: LocalBudgetItem[]): LocalBudgetItem | null => {
      for (const item of items) {
        if (item.budgetItemID === itemId && item.isRelatedItem) {
          // 从相关预算项的ID提取原始预算项ID (去掉 'related_' 前缀)
          const originalItemId = item.budgetItemID.replace('related_', '');
          if (originalItemId) {
            return findBudgetItem(items, originalItemId);
          }
        }
        if (item.budgetItemChildren) {
          const found = findOriginalItem(item.budgetItemChildren);
          if (found) return found;
        }
      }
      return null;
    };
    
    // 更新本地状态
    setBudgetItems(prevItems => {
      let updatedItems = [...prevItems];
      
      if (field === 'budgetItemAmount' || field === 'budgetItemUsedAmount') {
        const numericValue = Number(value) || 0;
        
        // 查找当前项目及其所有父项
        const findItemAndParents = (items: LocalBudgetItem[], targetId: string): LocalBudgetItem[] => {
          for (const item of items) {
            if (item.budgetItemID === targetId) {
              return [item];
            }
            if (item.budgetItemChildren) {
              const found = findItemAndParents(item.budgetItemChildren, targetId);
              if (found.length > 0) {
                return [...found, item];
              }
            }
          }
          return [];
        };

        // 获取需要更新的项目链
        const itemChain = findItemAndParents(updatedItems, itemId);
        
        // 更新当前项目和所有父项
        const updateItemChain = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
          return items.map(item => {
            // 如果是当前修改的项目
            if (item.budgetItemID === itemId) {
              const updatedItem = {
                ...item,
                [field]: numericValue
              };
              // 将当前项添加到更新列表
              itemsToUpdate.set(item.budgetItemID, {
                [field]: numericValue
              });
              return updatedItem;
            }
            
            // 如果是父项目之一
            if (item.budgetItemChildren && itemChain.some(chainItem => chainItem.budgetItemID === item.budgetItemID)) {
              const updatedChildren = updateItemChain(item.budgetItemChildren);
              const totalAmount = updatedChildren.reduce((sum, child) => sum + Number(child.budgetItemAmount || 0), 0);
              const totalUsedAmount = updatedChildren.reduce((sum, child) => sum + Number(child.budgetItemUsedAmount || 0), 0);
              
              // 将父项添加到更新列表
              itemsToUpdate.set(item.budgetItemID, {
                budgetItemAmount: totalAmount,
                budgetItemUsedAmount: totalUsedAmount
              });
              
              return {
                ...item,
                budgetItemChildren: updatedChildren,
                budgetItemAmount: totalAmount,
                budgetItemUsedAmount: totalUsedAmount
              };
            }
            
            // 如果有子项但不是目标链中的项目
            if (item.budgetItemChildren) {
              return {
                ...item,
                budgetItemChildren: updateItemChain(item.budgetItemChildren)
              };
            }
            
            return item;
          });
        };
        
        updatedItems = updateItemChain(updatedItems);
      } else {
        // 处理其他字段的更新
        const updateItems = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
          return items.map(item => {
            if (item.budgetItemID === itemId) {
              let updatedItem = { ...item };

              // 如果是虚拟相关预算项，需要同步到原始预算项
              if (item.isRelatedItem) {
                // 从相关预算项的ID提取原始预算项ID (去掉 'related_' 前缀)
                const originalItemId = item.budgetItemID.replace('related_', '');
                if (originalItemId) {
                  // 使用当前的budgetItems状态查找原始预算项
                  const originalItem = findBudgetItem(budgetItems, originalItemId);
                  
                  if (originalItem) {
                    // 处理相关预算项的修改，先同步到本地原始预算项，再同步到服务器
                    console.log(`🔄 相关预算项修改，同步到原始预算项:`, {
                      relatedItemId: item.budgetItemID,
                      originalItemId: originalItemId,
                      field,
                      value
                    });
                    
                    // 处理不同字段的复杂逻辑
                    if (field === 'budgetItemUnitPrice' || field === 'budgetItemQuantity1' || field === 'budgetItemQuantity2') {
                      // 单价、数量字段修改：需要重新计算金额
                      const newUnitPrice = field === 'budgetItemUnitPrice' ? Number(value) || 0 : (originalItem.budgetItemUnitPrice || 0);
                      const newQuantity1 = field === 'budgetItemQuantity1' ? Number(value) || 1 : (originalItem.budgetItemQuantity1 || 1);
                      const newQuantity2 = field === 'budgetItemQuantity2' ? Number(value) || 1 : (originalItem.budgetItemQuantity2 || 1);
                      
                      // 只有叶子节点才计算金额
                      if (!originalItem.budgetItemChildren || originalItem.budgetItemChildren.length === 0) {
                        const newAmount = newUnitPrice * newQuantity1 * newQuantity2;
                        
                        console.log(`💰 重新计算原始预算项金额:`, {
                          originalItemId,
                          newUnitPrice,
                          newQuantity1,
                          newQuantity2,
                          newAmount
                        });
                        
                        // 同步到服务器
                        itemsToUpdate.set(originalItemId, {
                          [field]: Number(value),
                          budgetItemAmount: newAmount
                        });
                        
                                                 // 更新虚拟项显示
                         const tempUpdate: any = {};
                         tempUpdate[field] = value;
                         updatedItem = { ...updatedItem, ...tempUpdate };
                        
                                                 // 相关预算金额直接使用原始预算项的货币和金额
                         updatedItem.calculatedRelatedAmount = newAmount;
                         
                         console.log(`💰 更新相关预算金额:`, {
                           newAmount,
                           currency: originalItem.budgetItemCurrency || 'RMB'
                         });
                      } else {
                                                 // 有子项的预算项只更新字段值
                         itemsToUpdate.set(originalItemId, {
                           [field]: value
                         });
                         const tempUpdate: any = {};
                         tempUpdate[field] = value;
                         updatedItem = { ...updatedItem, ...tempUpdate };
                      }
                      
                    } else if (field === 'budgetItemCurrency') {
                      // 货币字段修改：需要汇率转换
                      const newCurrency = String(value);
                      const oldCurrency = originalItem.budgetItemCurrency || 'RMB';
                      
                      if (newCurrency !== oldCurrency) {
                        // 转换原始预算项的所有金额字段
                        const convertedUnitPrice = originalItem.budgetItemUnitPrice ? 
                          convertCurrency(originalItem.budgetItemUnitPrice, oldCurrency, newCurrency) : 0;
                        const convertedAmount = originalItem.budgetItemAmount ?
                          convertCurrency(originalItem.budgetItemAmount, oldCurrency, newCurrency) : 0;
                        const convertedUsedAmount = originalItem.budgetItemUsedAmount ?
                          convertCurrency(originalItem.budgetItemUsedAmount, oldCurrency, newCurrency) : 0;
                        
                        console.log(`💱 货币转换:`, {
                          originalItemId,
                          oldCurrency,
                          newCurrency,
                          convertedAmount
                        });
                        
                        // 更新用户行为记忆：记录最后使用的货币类型
                        const backendCurrency = convertToBackendCurrency(newCurrency);
                        if (backendCurrency) {
                          setServerSyncedField('userLastUsedCurrency', backendCurrency);
                        }
                        
                        // 同步到服务器
                        itemsToUpdate.set(originalItemId, {
                          budgetItemCurrency: newCurrency,
                          budgetItemUnitPrice: convertedUnitPrice,
                          budgetItemAmount: convertedAmount,
                          budgetItemUsedAmount: convertedUsedAmount,
                          budgetItemRemainingAmount: convertedAmount - convertedUsedAmount
                        });
                        
                        // 更新虚拟项显示
                        updatedItem.budgetItemCurrency = newCurrency;
                        updatedItem.budgetItemUnitPrice = convertedUnitPrice;
                        updatedItem.budgetItemAmount = convertedAmount;
                        
                                                 // 相关预算金额直接使用新货币，不再转换为父级货币
                         updatedItem.calculatedRelatedAmount = convertedAmount;
                         
                         console.log(`💱 更新相关预算金额（使用新货币）:`, {
                           newAmount: convertedAmount,
                           newCurrency
                         });
                      } else {
                                                 // 货币没变化，直接更新
                         const updateData: any = {};
                         updateData[field] = value;
                         itemsToUpdate.set(originalItemId, updateData);
                         const tempUpdate: any = {};
                         tempUpdate[field] = value;
                         updatedItem = { ...updatedItem, ...tempUpdate };
                      }
                      
                    } else if (field === 'budgetItemUnit1' || field === 'budgetItemUnit2') {
                      const strVal = String(value);
                      let nextU1 = field === 'budgetItemUnit1' ? strVal : (item.budgetItemUnit1 || '--');
                      let nextU2 = field === 'budgetItemUnit2' ? strVal : (item.budgetItemUnit2 || '--');
                      if (field === 'budgetItemUnit1' && strVal === '--') {
                        nextU2 = '--';
                      }
                      let nextQ1 = originalItem.budgetItemQuantity1 || 1;
                      let nextQ2 = originalItem.budgetItemQuantity2 || 1;
                      if (field === 'budgetItemUnit1' && strVal === '--') {
                        nextQ1 = 1;
                        nextQ2 = 1;
                      } else if (field === 'budgetItemUnit2' && strVal === '--') {
                        nextQ2 = 1;
                      }
                      const serverU1 = nextU1 === '--' ? null : nextU1;
                      const serverU2 = nextU2 === '--' ? null : nextU2;
                      const syncPatch: Record<string, unknown> = {
                        budgetItemUnit1: serverU1,
                        budgetItemUnit2: serverU2,
                        budgetItemQuantity1: nextQ1,
                        budgetItemQuantity2: nextQ2,
                      };
                      if (!originalItem.budgetItemChildren || originalItem.budgetItemChildren.length === 0) {
                        const np = originalItem.budgetItemUnitPrice || 0;
                        const newAmount = np * nextQ1 * nextQ2;
                        syncPatch.budgetItemAmount = newAmount;
                        updatedItem = {
                          ...updatedItem,
                          budgetItemUnit1: nextU1,
                          budgetItemUnit2: nextU2,
                          budgetItemQuantity1: nextQ1,
                          budgetItemQuantity2: nextQ2,
                          budgetItemAmount: newAmount,
                          calculatedRelatedAmount: newAmount,
                        };
                      } else {
                        updatedItem = {
                          ...updatedItem,
                          budgetItemUnit1: nextU1,
                          budgetItemUnit2: nextU2,
                          budgetItemQuantity1: nextQ1,
                          budgetItemQuantity2: nextQ2,
                        };
                      }
                      itemsToUpdate.set(originalItemId, syncPatch);
                    } else {
                                             // 其他字段（名称、备注等）直接同步
                       itemsToUpdate.set(originalItemId, {
                         [field]: value
                       });
                       const tempUpdate: any = {};
                       tempUpdate[field] = value;
                       updatedItem = { ...updatedItem, ...tempUpdate };
                    }
                    
                    // 标记需要重新加载数据以同步本地原始预算项的显示
                    (updatedItem as any).__needsRelatedSync = true;
                  }
                }
                
                return updatedItem;
              }
              
              // 非虚拟项的常规处理
              
              // 处理原始预算项本身的更新
              if (field === 'budgetItemUnitPrice' || field === 'budgetItemQuantity1' || field === 'budgetItemQuantity2') {
                // 单价、数量字段需要重新计算金额
                const unitPrice = field === 'budgetItemUnitPrice' ? Number(value) || 0 : (item.budgetItemUnitPrice || 0);
                const quantity1 = field === 'budgetItemQuantity1' ? Number(value) || 1 : (item.budgetItemQuantity1 || 1);
                const quantity2 = field === 'budgetItemQuantity2' ? Number(value) || 1 : (item.budgetItemQuantity2 || 1);
                
                if (!item.budgetItemChildren || item.budgetItemChildren.length === 0) {
                  const calculatedAmount = unitPrice * quantity1 * quantity2;
                  
                  // 创建临时更新对象以处理字段赋值
                  const fieldUpdate: any = {};
                  fieldUpdate[field] = Number(value);
                  
                  updatedItem = {
                    ...updatedItem,
                    ...fieldUpdate,
                    budgetItemAmount: calculatedAmount
                  };
                  
                  itemsToUpdate.set(item.budgetItemID, {
                    [field]: Number(value),
                    budgetItemAmount: calculatedAmount
                  });
                  
                  // 标记需要更新父级预算项
                  return {
                    ...updatedItem,
                    __needsParentUpdate: true
                  };
                } else {
                  // 有子项的预算项也需要更新本地显示
                  const fieldUpdate: any = {};
                  fieldUpdate[field] = value;
                  
                  updatedItem = {
                    ...updatedItem,
                    ...fieldUpdate
                  };
                  
                  itemsToUpdate.set(item.budgetItemID, {
                    [field]: value
                  });
                  return updatedItem;
                }
                             } else if (field as string === 'budgetItemAmount' || field as string === 'calculatedRelatedAmount') {
                 // 小计预算字段和相关预算金额字段不可编辑
                 return item;
              } else if (field === 'budgetItemCurrency') {
                // 处理货币变更
                const oldCurrency = item.budgetItemCurrency || 'RMB';
                const newCurrency = String(value);
                
                if (oldCurrency !== newCurrency) {
                  const convertedUnitPrice = item.budgetItemUnitPrice ? 
                    convertCurrency(item.budgetItemUnitPrice, oldCurrency, newCurrency) : 0;
                  const convertedAmount = item.budgetItemAmount ?
                    convertCurrency(item.budgetItemAmount, oldCurrency, newCurrency) : 0;
                  const convertedUsedAmount = item.budgetItemUsedAmount ?
                    convertCurrency(item.budgetItemUsedAmount, oldCurrency, newCurrency) : 0;
                  
                  // 更新用户行为记忆：记录最后使用的货币类型
                  const backendCurrency = convertToBackendCurrency(newCurrency);
                  if (backendCurrency) {
                    setServerSyncedField('userLastUsedCurrency', backendCurrency);
                  }
                  
                  updatedItem = {
                    ...item,
                    budgetItemCurrency: newCurrency,
                    budgetItemUnitPrice: convertedUnitPrice,
                    budgetItemAmount: convertedAmount,
                    budgetItemUsedAmount: convertedUsedAmount,
                    budgetItemRemainingAmount: convertedAmount - convertedUsedAmount
                  };
                  
                  itemsToUpdate.set(item.budgetItemID, {
                    budgetItemCurrency: newCurrency,
                    budgetItemUnitPrice: convertedUnitPrice,
                    budgetItemAmount: convertedAmount,
                    budgetItemUsedAmount: convertedUsedAmount,
                    budgetItemRemainingAmount: convertedAmount - convertedUsedAmount
                  });
                  
                  // 标记需要更新父级预算项和重新加载以同步相关预算项
                  return {
                    ...updatedItem,
                    __needsParentUpdate: true,
                    __needsRelatedSync: true
                  };
                } else {
                  return item;
                }
              } else if (field === 'budgetItemUnit1' || field === 'budgetItemUnit2') {
                const strVal = String(value);
                let nextU1 = field === 'budgetItemUnit1' ? strVal : (item.budgetItemUnit1 || '--');
                let nextU2 = field === 'budgetItemUnit2' ? strVal : (item.budgetItemUnit2 || '--');
                if (field === 'budgetItemUnit1' && strVal === '--') {
                  nextU2 = '--';
                }
                let nextQ1 = item.budgetItemQuantity1 || 1;
                let nextQ2 = item.budgetItemQuantity2 || 1;
                if (field === 'budgetItemUnit1' && strVal === '--') {
                  nextQ1 = 1;
                  nextQ2 = 1;
                } else if (field === 'budgetItemUnit2' && strVal === '--') {
                  nextQ2 = 1;
                }
                const serverU1 = nextU1 === '--' ? null : nextU1;
                const serverU2 = nextU2 === '--' ? null : nextU2;
                const unitPatch: Record<string, unknown> = {
                  budgetItemUnit1: serverU1,
                  budgetItemUnit2: serverU2,
                  budgetItemQuantity1: nextQ1,
                  budgetItemQuantity2: nextQ2,
                };
                if (!item.budgetItemChildren || item.budgetItemChildren.length === 0) {
                  const unitPrice = item.budgetItemUnitPrice || 0;
                  const calculatedAmount = unitPrice * nextQ1 * nextQ2;
                  updatedItem = {
                    ...item,
                    budgetItemUnit1: nextU1,
                    budgetItemUnit2: nextU2,
                    budgetItemQuantity1: nextQ1,
                    budgetItemQuantity2: nextQ2,
                    budgetItemAmount: calculatedAmount,
                  };
                  itemsToUpdate.set(item.budgetItemID, {
                    ...unitPatch,
                    budgetItemAmount: calculatedAmount,
                  });
                  return {
                    ...updatedItem,
                    __needsParentUpdate: true,
                  };
                }
                updatedItem = {
                  ...item,
                  budgetItemUnit1: nextU1,
                  budgetItemUnit2: nextU2,
                  budgetItemQuantity1: nextQ1,
                  budgetItemQuantity2: nextQ2,
                };
                itemsToUpdate.set(item.budgetItemID, unitPatch);
                return updatedItem;
              } else {
                // 其他字段的常规更新
                updatedItem = {
                  ...item,
                  [field]: value
                };
                
                itemsToUpdate.set(item.budgetItemID, {
                  [field]: value
                });
                return updatedItem;
              }
            }
            if (item.budgetItemChildren) {
              const updatedChildren = updateItems(item.budgetItemChildren);
              
              // 检查是否有子项需要更新父级
              const needsParentUpdate = updatedChildren.some(child => (child as any).__needsParentUpdate);
              
              // 检查是否有原始预算项需要同步到虚拟相关预算项
              const needsRelatedSync = updatedChildren.some(child => (child as any).__needsRelatedSync);
              
              if (needsParentUpdate) {
                // 分别计算普通子项和相关预算项
                const normalChildren = updatedChildren.filter(child => !child.isRelatedItem);
                
                // 使用汇率换算计算普通子项的金额总和（转换为父级货币）
                const parentCurrency = item.budgetItemCurrency || 'RMB';
                const childrenAmounts = normalChildren.map(child => ({
                  amount: typeof child.budgetItemAmount === 'string' ? 
                    parseFloat(child.budgetItemAmount) || 0 : 
                    child.budgetItemAmount || 0,
                  currency: child.budgetItemCurrency || 'RMB'
                }));
                const childrenUsedAmounts = normalChildren.map(child => ({
                  amount: typeof child.budgetItemUsedAmount === 'string' ? 
                    parseFloat(child.budgetItemUsedAmount) || 0 : 
                    child.budgetItemUsedAmount || 0,
                  currency: child.budgetItemCurrency || 'RMB'
                }));
                
                const totalAmount = sumAmountsInTargetCurrency(childrenAmounts, parentCurrency);
                const totalUsedAmount = sumAmountsInTargetCurrency(childrenUsedAmounts, parentCurrency);
                
                // 计算相关预算金额总和（转换为父级货币）
                const calculateRelatedAmountSum = (items: LocalBudgetItem[], targetCurrency: string): number => {
                  return items.reduce((sum, item) => {
                    // 如果是相关预算项，将其金额转换为目标货币
                    if (item.isRelatedItem && item.calculatedRelatedAmount) {
                      const convertedAmount = convertCurrency(
                        item.calculatedRelatedAmount,
                        item.budgetItemCurrency || 'RMB',
                        targetCurrency
                      );
                      return sum + convertedAmount;
                    }
                    // 如果是普通预算项，递归计算其子项的相关预算金额
                    const childrenAmount = item.budgetItemChildren ? 
                      calculateRelatedAmountSum(item.budgetItemChildren, targetCurrency) : 0;
                    return sum + childrenAmount;
                  }, 0);
                };
                
                // 计算所有子项的相关预算金额总和（转换为父级货币）
                const totalRelatedAmount = calculateRelatedAmountSum(updatedChildren, parentCurrency);
                
                // 将父项添加到更新列表
                const updateData: any = {
                  budgetItemAmount: totalAmount,
                  budgetItemUsedAmount: totalUsedAmount,
                  budgetItemRemainingAmount: totalAmount - totalUsedAmount
                };
                
                itemsToUpdate.set(item.budgetItemID, updateData);
                
                const updatedItem = {
                  ...item,
                  budgetItemChildren: updatedChildren.map(child => {
                    // 移除临时标记
                    const { __needsParentUpdate, __needsRelatedSync, ...cleanChild } = child as any;
                    return cleanChild;
                  }),
                  budgetItemAmount: totalAmount,
                  budgetItemUsedAmount: totalUsedAmount,
                  budgetItemRemainingAmount: totalAmount - totalUsedAmount,
                  // 标记这个父项也需要更新其父级
                  __needsParentUpdate: true
                };
                
                if (totalRelatedAmount > 0) {
                  (updatedItem as any).calculatedRelatedAmount = totalRelatedAmount;
                }
                
                return updatedItem;
              }
              
              // 如果有相关预算项需要同步，重新构建子项的相关预算项
              if (needsRelatedSync) {
                const cleanedChildren = updatedChildren.map(child => {
                  const { __needsRelatedSync, ...cleanChild } = child as any;
                  return cleanChild;
                });
                
                return {
                  ...item,
                  budgetItemChildren: cleanedChildren,
                  __needsRelatedSync: true // 向上传递标记
                };
              }
              
              return {
                ...item,
                budgetItemChildren: updatedChildren
              };
            }
            return item;
          });
        };
        updatedItems = updateItems(updatedItems);
        
        // 检查是否需要重新构建相关预算项
        const checkRelatedSync = (items: LocalBudgetItem[]): boolean => {
          return items.some(item => {
            if ((item as any).__needsRelatedSync) return true;
            if (item.budgetItemChildren) {
              return checkRelatedSync(item.budgetItemChildren);
            }
            return false;
          });
        };
        
        const needsFullRelatedSync = checkRelatedSync(updatedItems);
        
        // 清理所有临时标记
        const cleanupTempMarkers = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
          return items.map(item => {
            const { __needsParentUpdate, __needsRelatedSync, ...cleanItem } = item as any;
            if (cleanItem.budgetItemChildren) {
              cleanItem.budgetItemChildren = cleanupTempMarkers(cleanItem.budgetItemChildren);
            }
            return cleanItem;
          });
        };
        updatedItems = cleanupTempMarkers(updatedItems);
        
        // 如果修改的是预算代码，需要重新排序
        if (field === 'budgetItemCode') {
          console.log(`🔄 预算代码已修改，重新排序预算项`);
          updatedItems = sortItemsByCode(updatedItems);
        }
        
        // 如果需要同步相关预算项，使用优化的处理方式
        if (needsFullRelatedSync) {
          console.log(`🔄 需要同步相关预算项，使用本地状态更新`);
          // 使用本地状态更新而不是重新加载，避免界面闪烁
          // 只是标记需要更新相关预算项，实际更新已经在上面的逻辑中完成
        }
      }
      
      // 在状态更新完成后，执行服务器更新
      setTimeout(async () => {
        // 过滤掉虚拟相关预算项（ID以related_开头的项目）和相关预算容器
        const realItemsToUpdate = new Map<string, UpdateBudgetItem>();
        Array.from(itemsToUpdate.entries()).forEach(([itemId, updateData]) => {
          // 跳过虚拟相关预算项和相关预算容器
          if (!itemId.startsWith('related_')) {
            realItemsToUpdate.set(itemId, updateData);
          }
        });

        // 按层级排序项目，确保子项在父项之前更新
        const sortedItems = Array.from(realItemsToUpdate.entries()).sort((a, b) => {
          const findLevel = (items: LocalBudgetItem[], id: string): number => {
            for (const item of items) {
              if (item.budgetItemID === id) return Number(item.budgetItemLevel) || 0;
              if (item.budgetItemChildren) {
                const level = findLevel(item.budgetItemChildren, id);
                if (level !== -1) return level;
              }
            }
            return -1;
          };
          
          const levelA = findLevel(budgetItems, a[0]);
          const levelB = findLevel(budgetItems, b[0]);
          return levelB - levelA; // 从深到浅排序
        });

        // 按顺序更新服务器数据
        for (const [itemId, updateData] of sortedItems) {
          try {
            await updateBudgetItem(itemId, updateData);
          } catch (error) {
            console.error(`更新预算项 ${itemId} 失败:`, error);
          }
        }

        // 如果修改的是预算项金额或相关字段，需要更新预算总金额
        if ((field === 'budgetItemAmount' || field === 'budgetItemUnitPrice' || field === 'budgetItemQuantity1' || field === 'budgetItemQuantity2') && budgetID) {
          try {
            // 重新获取最新的预算项数据
            setBudgetItems(prevItems => {
              const newTotalAmount = calculateTotalAmount(prevItems);
              
              // 异步更新预算总金额到数据库
              console.log('正在更新预算总金额:', newTotalAmount);
              updateBudget(budgetID, { 
                budgetTotalAmount: newTotalAmount 
              }).then((result) => {
                setTotalAmount(newTotalAmount);
                console.log('预算总金额更新成功:', newTotalAmount, result);
              }).catch(err => {
                console.error('更新预算总金额失败:', err);
              });
              
              return prevItems;
            });
          } catch (error) {
            console.error('计算预算总金额失败:', error);
          }
        }
      }, 0);

      return updatedItems;
    });


  };

  // 处理全选
  const handleSelectAll = (checked: boolean) => {
    setBudgetItems(items => {
      const updateItem = (item: LocalBudgetItem): LocalBudgetItem => {
        return {
          ...item,
          budgetItemChecked: checked,
          budgetItemChildren: item.budgetItemChildren?.map(updateItem)
        };
      };
      return items.map(updateItem);
    });
  };

  // 处理单行选择
  const handleSelectItem = (itemId: string, checked: boolean) => {
    setBudgetItems(items => {
      const updateItem = (item: LocalBudgetItem): LocalBudgetItem => {
        if (item.budgetItemID === itemId) {
          // 如果选中父级，同时选中所有子级
          const updatedItem = {
            ...item,
            budgetItemChecked: checked,
            budgetItemChildren: item.budgetItemChildren?.map(child => ({
              ...child,
              budgetItemChecked: checked,
              // 确保 budgetItemAmount 是数字类型
              budgetItemAmount: typeof child.budgetItemAmount === 'string' ? 
                parseFloat(child.budgetItemAmount) || 0 : 
                child.budgetItemAmount || 0
            }))
          };
          // 确保父级的 budgetItemAmount 也是数字类型
          updatedItem.budgetItemAmount = typeof updatedItem.budgetItemAmount === 'string' ? 
            parseFloat(updatedItem.budgetItemAmount) || 0 : 
            updatedItem.budgetItemAmount || 0;
          return updatedItem;
        }
        if (item.budgetItemChildren) {
          const updatedChildren = item.budgetItemChildren.map(child => 
            updateItem(child)
          );
          // 如果所有子级都被选中，父级也要被选中
          const allChildrenChecked = updatedChildren.every(child => child.budgetItemChecked);
          const updatedItem = {
            ...item,
            budgetItemChildren: updatedChildren,
            budgetItemChecked: allChildrenChecked,
            // 确保 budgetItemAmount 是数字类型
            budgetItemAmount: typeof item.budgetItemAmount === 'string' ? 
              parseFloat(item.budgetItemAmount) || 0 : 
              item.budgetItemAmount || 0
          };
          return updatedItem;
        }
        // 确保返回的单个项的 budgetItemAmount 也是数字类型
        return item;
      };
      return items.map(updateItem);
    });
  };

  // 检查是否全部选中
  const isAllSelected = () => {
    const checkItem = (item: LocalBudgetItem): boolean => {
      if (!item.budgetItemChecked) return false;
      if (item.budgetItemChildren) {
        return item.budgetItemChildren.every(checkItem);
      }
      return true;
    };
    return budgetItems.every(checkItem);
  };

  // 显示删除确认框
  const showDeleteConfirm = (itemId: string) => {
    setDeleteItemId(itemId);
    setDeleteModalVisible(true);
  };

  // 生成新的代码
  const generateNewCode = (parentItem: LocalBudgetItem) => {
    const parentCode = parentItem.budgetItemCode;
    const allChildren = parentItem.budgetItemChildren || [];
    // 只考虑普通子项，排除相关预算容器
    const siblings = allChildren.filter(child => !child.isRelatedContainer);
    
    // 如果没有普通子级，返回第一个子级代码
    if (siblings.length === 0) {
      return `${parentCode}01`;
    }

    // 找到最大的子级代码
    const maxCode = siblings.reduce((max, child) => {
      const childNum = parseInt(child.budgetItemCode.slice(-2));
      return childNum > max ? childNum : max;
    }, 0);

    // 生成新的代码，保证两位数
    const newNum = maxCode + 1;
    const newNumStr = newNum.toString().padStart(2, '0');
    return `${parentCode}${newNumStr}`;
  };

  // 添加新行
  const handleAddItem = async (parentId: string) => {
    try {
      const newItemId = generateBudgetItemId();
      const parentItem = findBudgetItem(budgetItems, parentId);
      
      if (!parentItem) {
        console.error('Parent item not found');
        return;
      }

      // 检查是否从叶子节点变成父级节点（在添加前检查）
      const existingChildren = parentItem.budgetItemChildren || [];
      const normalChildren = existingChildren.filter(child => !child.isRelatedContainer);
      const isBecomingParent = normalChildren.length === 0;

      // 获取用户最后使用的货币
      // 1. 优先使用 userLastUsedCurrency（用户行为记忆）- 从 CurrentUserContext 获取
      // 2. 如果不存在，使用 userPreferredCurrency（用户首选货币）
      // 3. 如果都不存在，使用 'RMB' 作为默认值
      const behaviorData = getBehaviorData();
      let currencyToUse: UserCurrency | undefined = behaviorData.userLastUsedCurrency || undefined;
      
      // 调试日志：从 CurrentUserContext 获取 userLastUsedCurrency
      if (currencyToUse) {
        console.log(`✅ [新建预算项] 从 CurrentUserContext 获取到 userLastUsedCurrency: ${currencyToUse}`);
      } else {
        console.log(`⚠️ [新建预算项] CurrentUserContext 中 userLastUsedCurrency 为空`);
      }
      
      let defaultCurrency = 'RMB' as import('./utils/currencyConverter').FrontendCurrency;
      if (currencyToUse) {
        defaultCurrency = convertToFrontendCurrency(currencyToUse);
      } else {
        try {
          const userInfo = getUserInfo();
          const config = resolveUserAppConfig(userInfo.userAppConfig, 'zh-CN');
          defaultCurrency = appCurrencyCodeToBudgetCurrency(config.currency);
        } catch (error) {
          console.warn('获取用户首选货币失败，使用默认货币 RMB:', error);
        }
      }

      const parseBudgetNumber = (v: unknown, fallback: number): number => {
        if (v === null || v === undefined || v === '') return fallback;
        const n = typeof v === 'string' ? parseFloat(v) : Number(v);
        return Number.isFinite(n) ? n : fallback;
      };

      // 无子项的叶子行首次添加子预算：继承名称（加后缀 '）、负责人、单价、数量、单位（及货币），金额按 单价×数量1×数量2
      let childName = '';
      let childUnit1 = '--';
      let childUnit2 = '--';
      let childUnitPrice = 0;
      let childQuantity1 = 1;
      let childQuantity2 = 1;
      let childCurrency: FrontendCurrency = defaultCurrency;
      if (isBecomingParent) {
        const pn = (parentItem.budgetItemName || '').trim();
        if (pn) {
          childName = `${pn}'`;
        }
        const pu1 = parentItem.budgetItemUnit1;
        if (pu1 != null && String(pu1).trim() !== '') {
          childUnit1 = String(pu1);
        }
        const pu2 = parentItem.budgetItemUnit2;
        if (pu2 != null && String(pu2).trim() !== '') {
          childUnit2 = String(pu2);
        }
        childUnitPrice = parseBudgetNumber(parentItem.budgetItemUnitPrice, 0);
        childQuantity1 = parseBudgetNumber(parentItem.budgetItemQuantity1, 1);
        childQuantity2 = parseBudgetNumber(parentItem.budgetItemQuantity2, 1);
        const pc = parentItem.budgetItemCurrency;
        if (pc != null && String(pc).trim() !== '') {
          childCurrency = pc as FrontendCurrency;
        }
      }
      const childAmount = childUnitPrice * childQuantity1 * childQuantity2;
      // 界面「负责人」列读的是 budgetItemOwnerID；Assignee 可能未同步，故 Owner 优先并互相兜底
      const inheritedOwnerId = parentItem.budgetItemOwnerID;
      const inheritedAssigneeId =
        parentItem.budgetItemAssigneeID ?? (inheritedOwnerId ? inheritedOwnerId : null);

      const newCode = generateNewCode(parentItem);
      const newItem: LocalBudgetItem = {
        budgetItemID: newItemId,
        budgetItemCode: newCode,
        budgetItemName: childName,
        budgetItemBelongBudgetID: budgetID!,
        budgetItemParentID: parentId,
        budgetItemAmount: childAmount,
        budgetItemUsedAmount: 0,
        budgetItemRemainingAmount: childAmount,
        budgetItemProgressType: '按时间进度',
        budgetItemCurrency: childCurrency,
        budgetItemUnit1: childUnit1,
        budgetItemUnit2: childUnit2,
        budgetItemUnitPrice: childUnitPrice,
        budgetItemQuantity1: childQuantity1,
        budgetItemQuantity2: childQuantity2,
        calculatedRelatedAmount: 0,
        budgetItemRelatedID: parentItem.budgetItemID,
        budgetItemOwnerID: inheritedOwnerId,
        budgetItemAssigneeID: inheritedAssigneeId,
        budgetItemPartners: [],
        budgetItemRemark: '',
        budgetItemCreateTime: new Date().toISOString(),
        budgetItemUpdateTime: new Date().toISOString(),
        budgetItemChildren: [],
        budgetItemExpanded: true,
        budgetItemChecked: false,
        budgetItemFilmCode: null,
        budgetItemStatus: '预算中',
        budgetItemDataStatus: '正常',
        budgetItemLevel: parentItem.budgetItemLevel + 1
      };

      // 更新本地状态
      setBudgetItems(prevItems => {
        const updateItems = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
          return items.map(item => {
            if (item.budgetItemID === parentId) {
              // 分离普通子项和相关预算容器
              const existingChildren = item.budgetItemChildren || [];
              const normalChildren = existingChildren.filter(child => !child.isRelatedContainer);
              const relatedContainers = existingChildren.filter(child => child.isRelatedContainer);
              
              // 添加新子项
              const updatedChildren = [...normalChildren, newItem, ...relatedContainers];
              
              let updatedParentItem = {
                ...item,
                budgetItemExpanded: true, // 确保父级展开
                budgetItemChildren: updatedChildren
              };
              
              // 如果从叶子节点变成父级节点，清空相关字段
              if (isBecomingParent) {
                updatedParentItem = {
                  ...updatedParentItem,
                  budgetItemUnit1: null as any,        // 清空单位¹
                  budgetItemUnit2: null as any,        // 清空单位²
                  budgetItemUnitPrice: null as any,   // 清空单价
                  budgetItemQuantity1: null as any,   // 清空数量1
                  budgetItemQuantity2: null as any,   // 清空数量2
                  budgetItemRemark: null as any,      // 清空备注
                  budgetItemRelatedID: null as any,   // 清空相关预算ID
                  budgetItemAmount: 0,              // 重置金额为0
                  budgetItemUsedAmount: 0,          // 重置已使用金额
                  budgetItemRemainingAmount: 0,     // 重置剩余金额
                };
                
              }
              
              return updatedParentItem;
            }
            if (item.budgetItemChildren) {
              return {
                ...item,
                budgetItemChildren: updateItems(item.budgetItemChildren)
              };
            }
            return item;
          });
        };

        return updateParentAmounts(updateItems(prevItems));
      });

      // 保存新子项到数据库
      const { budgetItemChildren, budgetItemExpanded, budgetItemChecked, ...rest } = newItem;
      const serverItem: ServerUpdateItem = {
        ...rest,
        budgetItemAssigneeID: rest.budgetItemAssigneeID || undefined
      };
      
      await updateBudgetItem(newItem.budgetItemID, serverItem, true);

      // 如果父项从叶子节点变成父级节点，同时更新父项的字段
      if (isBecomingParent) {
        setTimeout(async () => {
          try {
            const clearFieldsData = {
              budgetItemUnit1: null,             // 清空单位¹
              budgetItemUnit2: null,             // 清空单位²
              budgetItemUnitPrice: null,        // 清空单价
              budgetItemQuantity1: null,        // 清空数量1
              budgetItemQuantity2: null,        // 清空数量2
              budgetItemRemark: null,           // 清空备注
              budgetItemRelatedID: null,        // 清空相关预算ID
              budgetItemAmount: 0,              // 重置金额为0
              budgetItemUsedAmount: 0,          // 重置已使用金额
              budgetItemRemainingAmount: 0,     // 重置剩余金额
            };
            await updateBudgetItem(parentId, clearFieldsData as any);
          } catch (error) {
            console.error(`❌ 父项字段清空更新失败:`, error);
          }
        }, 100);
      }

      // 更新预算总金额
      if (budgetID) {
        try {
          setBudgetItems(prevItems => {
            const newTotalAmount = calculateTotalAmount(prevItems);
            
            // 异步更新预算总金额到数据库
            updateBudget(budgetID, { 
              budgetTotalAmount: newTotalAmount 
            }).then(() => {
              setTotalAmount(newTotalAmount);
            }).catch(err => {
              console.error('更新预算总金额失败:', err);
            });
            
            return prevItems;
          });
        } catch (error) {
          console.error('计算预算总金额失败:', error);
        }
      }

    } catch (error) {
      console.error('添加预算项失败:', error);
    }
  };

  const handleAddTopItem = async () => {
    try {
      const newItemId = generateBudgetItemId();
      const maxCode = budgetItems
        .filter(item => !item.budgetItemParentID)
        .length > 0 
          ? Math.max(...budgetItems
              .filter(item => !item.budgetItemParentID)
              .map(item => parseInt(item.budgetItemCode) || 0))
          : 0;
      
      const newCode = (maxCode + 1).toString().padStart(2, '0');
      
      // 获取用户最后使用的货币
      // 1. 优先使用 userLastUsedCurrency（用户行为记忆）- 从 CurrentUserContext 获取
      // 2. 如果不存在，使用 userPreferredCurrency（用户首选货币）
      // 3. 如果都不存在，使用 'RMB' 作为默认值
      const behaviorData = getBehaviorData();
      let currencyToUse: UserCurrency | undefined = behaviorData.userLastUsedCurrency || undefined;
      
      // 调试日志：从 CurrentUserContext 获取 userLastUsedCurrency
      if (currencyToUse) {
        console.log(`✅ [新建预算项] 从 CurrentUserContext 获取到 userLastUsedCurrency: ${currencyToUse}`);
      } else {
        console.log(`⚠️ [新建预算项] CurrentUserContext 中 userLastUsedCurrency 为空`);
      }
      
      let defaultCurrency = 'RMB' as import('./utils/currencyConverter').FrontendCurrency;
      if (currencyToUse) {
        defaultCurrency = convertToFrontendCurrency(currencyToUse);
      } else {
        try {
          const userInfo = getUserInfo();
          const config = resolveUserAppConfig(userInfo.userAppConfig, 'zh-CN');
          defaultCurrency = appCurrencyCodeToBudgetCurrency(config.currency);
        } catch (error) {
          console.warn('获取用户首选货币失败，使用默认货币 RMB:', error);
        }
      }
      
      const newItem: LocalBudgetItem = {
        budgetItemID: newItemId,
        budgetItemCode: newCode,
        budgetItemName: '',
        budgetItemBelongBudgetID: budgetID!,
        budgetItemParentID: null,
        budgetItemAmount: 0,
        budgetItemUsedAmount: 0,
        budgetItemRemainingAmount: 0,
        budgetItemProgressType: '按时间进度',
        budgetItemCurrency: defaultCurrency,
        budgetItemUnit1: '--',
        budgetItemUnit2: '--',
        budgetItemUnitPrice: 0,
        budgetItemQuantity1: 1,
        budgetItemQuantity2: 1,
        calculatedRelatedAmount: 0,
        budgetItemRelatedID: null,
        budgetItemAssigneeID: null,
        budgetItemPartners: [],
        budgetItemRemark: '',
        budgetItemCreateTime: new Date().toISOString(),
        budgetItemUpdateTime: new Date().toISOString(),
        budgetItemChildren: [],
        budgetItemExpanded: true,
        budgetItemChecked: false,
        budgetItemFilmCode: null,
        budgetItemStatus: '预算中',
        budgetItemDataStatus: '正常',
        budgetItemLevel: 0
      };

      setBudgetItems(prevItems => [...prevItems, newItem]);
      const { budgetItemChildren, budgetItemExpanded, budgetItemChecked, ...rest } = newItem;
      const serverItem: ServerUpdateItem = {
        ...rest,
        budgetItemAssigneeID: rest.budgetItemAssigneeID || undefined
      };
      
      await updateBudgetItem(newItem.budgetItemID, serverItem, true);

      // 更新预算总金额
      if (budgetID) {
        try {
          setBudgetItems(prevItems => {
            const newTotalAmount = calculateTotalAmount(prevItems);
            
            // 异步更新预算总金额到数据库
            updateBudget(budgetID, { 
              budgetTotalAmount: newTotalAmount 
            }).then(() => {
              setTotalAmount(newTotalAmount);
              console.log('添加顶级预算项后，预算总金额已更新:', newTotalAmount);
            }).catch(err => {
              console.error('更新预算总金额失败:', err);
            });
            
            return prevItems;
          });
        } catch (error) {
          console.error('计算预算总金额失败:', error);
        }
      }

    } catch (error) {
      console.error('添加顶级预算项失败:', error);
    }
  };

  // 处理全局展开/收起
  const handleToggleAll = () => {
    setIsAllExpanded(!isAllExpanded);
    setBudgetItems(items => {
      const updateItem = (item: LocalBudgetItem): LocalBudgetItem => {
        return {
          ...item,
          budgetItemExpanded: !isAllExpanded,
          budgetItemChildren: item.budgetItemChildren?.map(updateItem)
        };
      };
      return items.map(updateItem);
    });
  };

  // 渲染展开图标
  const renderExpandIcon = (expanded: boolean | undefined) => {
    return <img src={expandIcon} alt="expand" className="expand-img" />;
  };



  // 修改用户信息渲染组件 - 从 GlobalUsersContext 获取，显示圆形头像+昵称首字
  const UserInfo: React.FC<{ userId: string; itemId: string; isRelatedContainer: boolean }> = ({ userId, itemId, isRelatedContainer }) => {
    const { getUser } = useGlobalUsers();
    const user = userId ? getUser(userId) : null;

    // 获取用户昵称首字
    const getUserInitial = () => {
      if (!user) return 'U';
      const name = user.userNickName || user.userName || '';
      if (!name) return 'U';
      // 如果是中文，取第一个字符
      if (/[\u4e00-\u9fa5]/.test(name)) {
        return name.charAt(0);
      }
      // 如果是英文，取第一个字符的大写
      return name.charAt(0).toUpperCase();
    };

    const handleClick = () => {
      if (!isRelatedContainer && !activeUserSearchId) {
        setActiveUserSearchId(itemId);
      }
    };

    if (!userId) return null;
    if (!user) {
      return (
        <div 
          className="budgetItemAssignee-user-info empty"
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        ></div>
      );
    }

    return (
      <div 
        className="budgetItemAssignee-user-info"
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="budgetItemAssignee-user-avatar-initial">
          {getUserInitial()}
        </div>
        <span className="budgetItemAssignee-user-name">{user.userNickName || user.userName}</span>
      </div>
    );
  };

  // 处理负责人更新
  const handleAssigneeChange = async (itemId: string, newOwnerId: string | null, options?: {
    updateChildren?: boolean;
    updateAllSameAssignee?: boolean;
  }) => {
    try {
      const item = findBudgetItem(budgetItems, itemId);
      if (!item) return;

      const oldOwnerId = item.budgetItemOwnerID;
      const updates: Promise<any>[] = [];

      if (options?.updateChildren) {
        // 更新所有子预算项
        const updateChildrenOwner = (items: LocalBudgetItem[]) => {
          items.forEach(item => {
            updates.push(updateBudgetItem(item.budgetItemID, {
              budgetItemOwnerID: newOwnerId || undefined
            }));
            if (item.budgetItemChildren) {
              updateChildrenOwner(item.budgetItemChildren);
            }
          });
        };

        if (item.budgetItemChildren) {
          updateChildrenOwner(item.budgetItemChildren);
        }
      }

      if (options?.updateAllSameAssignee && oldOwnerId) {
        // 更新所有相同负责人的预算项
        const updateSameOwner = (items: LocalBudgetItem[]) => {
          items.forEach(item => {
            if (item.budgetItemOwnerID === oldOwnerId) {
              updates.push(updateBudgetItem(item.budgetItemID, {
                budgetItemOwnerID: newOwnerId || undefined
              }));
            }
            if (item.budgetItemChildren) {
              updateSameOwner(item.budgetItemChildren);
            }
          });
        };

        updateSameOwner(budgetItems);
      }

      // 更新当前项
      updates.push(updateBudgetItem(itemId, {
        budgetItemOwnerID: newOwnerId || undefined
      }));

      // 等待所有更新完成
      await Promise.all(updates);

      // 数据已通过统一更新 hook 自动更新缓存，缓存订阅会自动更新 UI

    } catch (err: any) {
      console.error('更新负责人失败:', err);
      setError(err.message || '更新负责人失败');
    }
  };

  // 查找预算项的辅助函数
  const findBudgetItem = (items: LocalBudgetItem[], itemId: string): LocalBudgetItem | null => {
    for (const item of items) {
      if (item.budgetItemID === itemId) return item;
      if (item.budgetItemChildren) {
        const found = findBudgetItem(item.budgetItemChildren, itemId);
        if (found) return found;
      }
    }
    return null;
  };

  // findRelatedItems 函数已移除，现在由 useBudgetDataLoader 处理数据加载和树构建
  // 相关预算项的处理在 useBudgetDataLoader 的 buildTree 函数中完成


  // 递归查找预算项
  const findBudgetItemById = useCallback((items: LocalBudgetItem[], itemId: string): LocalBudgetItem | null => {
    for (const item of items) {
      if (item.budgetItemID === itemId) {
        return item;
      }
      if (item.budgetItemChildren) {
        const found = findBudgetItemById(item.budgetItemChildren, itemId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // 递归排序函数（按预算代码排序）
  const sortItemsByCode = useCallback((items: LocalBudgetItem[]): LocalBudgetItem[] => {
    // 对当前层级的项目排序
    const sorted = [...items].sort((a, b) => {
      const aCode = a.budgetItemCode || '';
      const bCode = b.budgetItemCode || '';
      return aCode.localeCompare(bCode);
    });
    
    // 递归排序每个项目的子项
    return sorted.map(item => {
      if (item.budgetItemChildren && item.budgetItemChildren.length > 0) {
        return {
          ...item,
          budgetItemChildren: sortItemsByCode(item.budgetItemChildren)
        };
      }
      return item;
    });
  }, []);

  // 查找父级预算代码
  const findParentCode = useCallback((item: LocalBudgetItem, allItems: LocalBudgetItem[]): string | null => {
    if (!item.budgetItemParentID) {
      return null; // 顶级项，没有父级
    }
    
    // 递归查找父级
    const findParent = (items: LocalBudgetItem[], parentID: string): LocalBudgetItem | null => {
      for (const currentItem of items) {
        if (currentItem.budgetItemID === parentID) {
          return currentItem;
        }
        if (currentItem.budgetItemChildren) {
          const found = findParent(currentItem.budgetItemChildren, parentID);
          if (found) return found;
        }
      }
      return null;
    };
    
    const parentItem = findParent(allItems, item.budgetItemParentID);
    return parentItem ? parentItem.budgetItemCode : null;
  }, []);

  // 判断是否有真正的子项（排除关联预算项和关联预算容器）
  const hasRealChildren = useCallback((item: LocalBudgetItem): boolean => {
    if (!item.budgetItemChildren || item.budgetItemChildren.length === 0) {
      return false;
    }
    // 检查是否有非关联预算项的子项
    return item.budgetItemChildren.some(child => !child.isRelatedItem && !child.isRelatedContainer);
  }, []);

  // 渲染预算项
  const renderItem = (item: LocalBudgetItem) => {
    const hasChildren = hasRealChildren(item);
    // 对于关联预算容器行，检查是否有任何子项（包括关联预算项）来显示展开箭头
    const shouldShowExpand = item.isRelatedContainer 
      ? (item.budgetItemChildren && item.budgetItemChildren.length > 0)
      : hasChildren;
    return (
      <React.Fragment key={item.budgetItemID}>
        <tr className={`budget-item ${item.isRelatedItem ? 'related-item' : ''} ${item.isRelatedContainer ? 'related-container' : ''}`} data-level={item.budgetItemLevel}>
          <td className="checkbox-cell">
            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                checked={item.budgetItemChecked}
                onChange={(e) => handleSelectItem(item.budgetItemID, e.target.checked)}
              />
            </div>
          </td>
          <td className="expand-cell">
            <div className="expand-wrapper">
              {shouldShowExpand && (
                <span 
                  className={`expand-icon ${item.budgetItemExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleExpand(item.budgetItemID)}
                >
                  {renderExpandIcon(item.budgetItemExpanded)}
                </span>
              )}
            </div>
          </td>
          <td className="budgetItemCode-cell">
            {item.isRelatedContainer ? (
              <div className="empty-cell"></div>
            ) : item.isRelatedItem ? (
              <div className="budget-code-content">
                <RelatedIcon className="related-item-icon" />
                <span>{item.budgetItemCode}</span>
              </div>
            ) : (
              <BudgetCodeEditableCell
                value={item.budgetItemCode}
                parentCode={findParentCode(item, budgetItems)}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemCode', value)}
                disabled={item.isRelatedContainer}
              />
            )}
          </td>
          <td 
            className={`budgetItemName-cell ${activeRelatedBudgetId === item.budgetItemID ? 'has-active-related' : ''}`}
            style={{ '--indent-level': item.budgetItemLevel } as React.CSSProperties}
          >
            <div className="budgetName-content">
              <EditableCell
                value={item.budgetItemName}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemName', value)}
                disabled={item.isRelatedContainer}
              />
              {!item.isRelatedItem && !item.isRelatedContainer && (
                <div className="action-buttons">
                  <button 
                    className="add-button"
                    onClick={() => handleAddItem(item.budgetItemID)}
                  >
                    <AddIcon className="button-icon" />
                  </button>
                  {/* 第一级预算项不显示删除按钮 */}
                  {item.budgetItemParentID && (
                    <button 
                      className="delete-button"
                      onClick={() => showDeleteConfirm(item.budgetItemID)}
                    >
                      <DeleteIcon className="button-icon" />
                    </button>
                  )}
                  {/* 关联按钮的显示隐藏状态与单价输入框一致：有真正的子项或是相关容器时不显示 */}
                  {!hasChildren && !item.isRelatedContainer && (
                    <div style={{ position: 'relative' }}>
                      <button 
                        className={`related-button ${activeRelatedBudgetId === item.budgetItemID ? 'active' : ''}`}
                        onClick={() => setActiveRelatedBudgetId(item.budgetItemID)}
                      >
                        <RelatedIcon className="button-icon" />
                      </button>
                      {activeRelatedBudgetId === item.budgetItemID && budgetID && (
                        <RelatedBudget
                          targetBudgetItemID={activeRelatedBudgetId}
                          budgetID={budgetID}
                          visible={true}
                          onClose={() => setActiveRelatedBudgetId(null)}
                          onRelated={(relatedItemID) => {
                            console.log('预算项关联成功:', relatedItemID);
                            // 数据已通过统一更新 hook 自动更新缓存，缓存订阅会自动更新 UI
                          }}
                          onCreated={(newItem) => {
                            console.log('新预算项创建成功:', newItem);
                            // 数据已通过统一更新 hook 自动更新缓存，缓存订阅会自动更新 UI
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
          <td className="budgetItemCurrency-cell">
            <CurrencySelect
              value={item.budgetItemCurrency || 'RMB'}
              onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemCurrency', value)}
              disabled={item.isRelatedContainer ? false : false}
            />
          </td>
          {/* <td className="budgetItemProgressType-cell">
            {item.isRelatedContainer ? (
              <div className="empty-cell"></div>
            ) : (
              <ProgressTypeSelect
                value={item.budgetItemProgressType}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemProgressType', value)}
              />
            )}
          </td> */}
          <td className="budgetItemUnitPrice-cell">
            {(hasChildren || item.isRelatedContainer) ? (
              <div className="empty-cell"></div>
            ) : (
              <EditableCell
                value={item.budgetItemUnitPrice || 0}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemUnitPrice', value)}
                type="number"
              />
            )}
          </td>
          <td className="budgetItemQuantity1-cell">
            {(hasChildren || 
             item.isRelatedContainer || 
             getUnitType(item.budgetItemUnit1 ?? undefined, item.budgetItemUnit2 ?? undefined) === 'none') ? (
              <div className="empty-cell"></div>
            ) : (
              <QuantityEditableCell
                value={item.budgetItemQuantity1 || 1}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemQuantity1', value)}
              />
            )}
          </td>
          <td className="budgetItemUnit-cell">
            {(hasChildren || item.isRelatedContainer) ? (
              <div className="empty-cell"></div>
            ) : (
              <BudgetUnitInput
                dropdownId={`budget-unit1-${item.budgetItemID}`}
                value={item.budgetItemUnit1 || '--'}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemUnit1', value)}
                presets={BUDGET_UNIT1_PRESETS}
                disabled={item.isRelatedContainer}
                translateUnitLabel={(u) => translateBudgetUnitPart(u, t)}
              />
            )}
          </td>
          <td className="budgetItemQuantity2-cell">
            {(hasChildren || 
             item.isRelatedContainer || 
             getUnitType(item.budgetItemUnit1 ?? undefined, item.budgetItemUnit2 ?? undefined) !== 'compound') ? (
              <div className="empty-cell"></div>
            ) : (
              <QuantityEditableCell
                value={item.budgetItemQuantity2 || 1}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemQuantity2', value)}
              />
            )}
          </td>
          <td className="budgetItemUnit2-cell">
            {(hasChildren || item.isRelatedContainer) ? (
              <div className="empty-cell"></div>
            ) : (
              <BudgetUnitInput
                dropdownId={`budget-unit2-${item.budgetItemID}`}
                value={item.budgetItemUnit2 || '--'}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemUnit2', value)}
                presets={BUDGET_UNIT2_PRESETS}
                disabled={item.isRelatedContainer}
                translateUnitLabel={(u) => translateBudgetUnitPart(u, t)}
              />
            )}
          </td>
          <td className="budgetItemAmount-cell">
            {item.isRelatedItem || item.isRelatedContainer ? (
              <div className="empty-cell"></div>
            ) : (
              <EditableCell
                value={formatCurrencyAmount(item.budgetItemAmount || 0, item.budgetItemCurrency || 'RMB', true)}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemAmount', value)}
                type="text"
                disabled={true}
              />
            )}
          </td>
          <td className="budgetItemRelatedBudgetAmount-cell">
            {item.isRelatedItem ? (
              <EditableCell
                value={formatCurrencyAmount(item.calculatedRelatedAmount || 0, 
                  // 相关预算项显示时应该使用原始预算项的货币
                  (() => {
                    const originalItemId = item.budgetItemID.replace('related_', '');
                    const originalItem = findBudgetItem(budgetItems, originalItemId);
                    return originalItem?.budgetItemCurrency || 'RMB';
                  })(), true)}
                onChange={(value) => handleCellChange(item.budgetItemID, 'calculatedRelatedAmount', value)}
                type="text"
                disabled={true}
              />
            ) : item.isRelatedContainer ? (
              <EditableCell
                value={formatCurrencyAmount(item.calculatedRelatedAmount || 0, item.budgetItemCurrency || 'RMB', true)}
                onChange={(value) => handleCellChange(item.budgetItemID, 'calculatedRelatedAmount', value)}
                type="text"
                disabled={true}
              />
            ) : item.calculatedRelatedAmount && item.calculatedRelatedAmount > 0 ? (
              <EditableCell
                value={formatCurrencyAmount(item.calculatedRelatedAmount, item.budgetItemCurrency || 'RMB', true)}
                onChange={(value) => handleCellChange(item.budgetItemID, 'calculatedRelatedAmount', value)}
                type="text"
                disabled={true}
              />
            ) : (
              <div className="empty-cell"></div>
            )}
          </td>
          {shouldShowAmountColumns() && (
            <>
              <td className="budgetItemUsedAmount-cell">
                <EditableCell
                  value={formatCurrencyAmount(item.budgetItemUsedAmount || 0, item.budgetItemCurrency || 'RMB', true)}
                  onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemUsedAmount', value)}
                  type="text"
                  disabled={true}
                />
              </td>
              <td className="budgetItemRemainingAmount-cell">
                <EditableCell
                  value={formatCurrencyAmount(item.budgetItemRemainingAmount || 0, item.budgetItemCurrency || 'RMB', true)}
                  onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemRemainingAmount', value)}
                  type="text"
                  disabled={true}
                />
              </td>
            </>
          )}
          <td className="budgetItemRemark-cell">
            {item.isRelatedContainer ? (
              <div className="empty-cell"></div>
            ) : (
              <EditableCell
                value={item.budgetItemRemark}
                onChange={(value) => handleCellChange(item.budgetItemID, 'budgetItemRemark', value)}
              />
            )}
          </td>
          <td className="budgetItemAssignee-cell">
            {item.isRelatedContainer ? (
              <div className="empty-cell"></div>
            ) : (
              <div className="assignee-cell">
                {item.budgetItemOwnerID ? (
                  <UserInfo 
                    userId={item.budgetItemOwnerID} 
                    itemId={item.budgetItemID}
                    isRelatedContainer={item.isRelatedContainer || false}
                  />
                ) : (
                  <div 
                    className="budgetItemAssignee-user-info empty"
                    onClick={() => !item.isRelatedContainer && !activeUserSearchId && setActiveUserSearchId(item.budgetItemID)}
                    style={{ cursor: 'pointer' }}
                  >
                    <AddCategoryIcon className="add-owner-icon" style={{ width: '16px', height: '16px' }} />
                  </div>
                )}
                {activeUserSearchId === item.budgetItemID && (
                  <div className="user-search-wrapper">
                    <UserSearch
                      value={item.budgetItemOwnerID || null}
                      onChange={(userID, options) => handleAssigneeChange(item.budgetItemID, userID, options)}
                      visible={activeUserSearchId === item.budgetItemID}
                      onClose={() => setActiveUserSearchId(null)}
                    />
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
        {item.budgetItemChildren && item.budgetItemExpanded && item.budgetItemChildren.map(child => renderItem(child))}
      </React.Fragment>
    );
  };


  // 处理预算状态更新
  const handleBudgetStatusUpdate = async () => {
    if (!budgetID || isUpdatingStatus) return;
    
    setIsUpdatingStatus(true);
    try {
      const newStatus = budgetStatus === '预算中' ? '已生效' : '预算中';
      await updateBudget(budgetID, { budgetStatus: newStatus });
      setBudgetStatus(newStatus);
    } catch (error) {
      console.error('更新预算状态失败:', error);
      setError('更新预算状态失败');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // 从缓存加载预算状态（useBudgetDataLoader 已经加载了数据）
  useEffect(() => {
    if (loadedBudgetStatus) {
      setBudgetStatus(loadedBudgetStatus);
    }
  }, [loadedBudgetStatus]);

  // 判断是否显示已使用金额和剩余金额列
  const shouldShowAmountColumns = () => {
    return budgetStatus !== '预算中' && budgetStatus !== '确认中';
  };

  // 判断单位类型
  const getUnitType = (u1: string | undefined, u2: string | undefined): 'none' | 'single' | 'compound' => {
    const unit1 = u1 && u1 !== '--' ? u1 : '';
    if (!unit1) return 'none';
    const unit2 = u2 && u2 !== '--' ? u2 : '';
    if (unit2) return 'compound';
    return 'single';
  };

  // 获取货币符号
  const getCurrencySymbol = (currency: string): string => {
    const currencySymbols: Record<string, string> = {
      'RMB': '¥',
      'HK$': 'HK$',
      'US$': 'US$',
      'EUR': '€',
      'JPY': '¥',
      'GBP': '£',
      'CAD': 'CAD$',
      'AUD': 'AUD$'
    };
    return currencySymbols[currency] || currency;
  };

  return (
    <div className="budget-layout">
      <div className="budget-main">
        <div className="budget-header">
          <div className="budgetTableName">
            <input
              type="text"
              placeholder={t('budget.untitled')}
              value={budgetName}
              onChange={(e) => setBudgetName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              className="budgetTableName-input"
            />
            <div className="budgetTotalAmountInfo">
            <span className="budgetTotalAmountTitle">{t('budget.mainCurrency')}{t('common.labelSeparator')}</span>
              <CurrencySelect
                value={totalAmountCurrency}
                onChange={(value) => handleMainCurrencyChange(value)}
                disabled={false}
              />
              <span className="budgetTotalAmountTitle">{t('common.pipeSeparator')}{t('budget.budgetTotalAmount')}{t('common.labelSeparator')}{getCurrencySymbol(totalAmountCurrency)}</span>
              <span className="budgetTotalAmount">
                {calculateTotalAmount(budgetItems).toLocaleString('en-HK', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  useGrouping: true
                })}
              </span>
            </div>
          </div>
          <div className="budget-actions">
            <button 
              className={budgetStatus === '已生效' ? "createBudget-actionButtons" : "releaseBudget-actionButtons"}
              onClick={handleBudgetStatusUpdate}
              disabled={isUpdatingStatus}
            >
              {budgetStatus === '已生效' ? (
                <ContrastIcon className="budget-action-icon" />
              ) : (
                <ReleaseIcon className="budget-action-icon" />
              )}
              {isUpdatingStatus ? t('budget.updating') : (budgetStatus === '已生效' ? t('budget.disable') : t('budget.publish'))}
            </button>
            <button className="createBudget-actionButtons">
              <ContrastIcon className="budget-action-icon" />
              {t('budget.compare')}
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* 标签行 - 固定位置 */}
        <div className="budget-category-tabs">
          {BUDGET_CATEGORY_IDS.map(category => (
            <div 
              key={category.id}
              className={`budget-category-tab ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              <category.Icon 
                className="category-icon"
                aria-label={t(`budget.${category.key}`)}
              />
              <span className="category-name">{t(`budget.${category.key}`)}</span>
            </div>
          ))}
          {/* 添加新预算项按钮 */}
          {!shouldHideAddTopItem() && (
            <div 
              className="budget-category-tab create-tab"
              onClick={handleAddTopItem}
            >
              <AddCategoryIcon className="category-icon" />
              <span className="category-name">{t('budget.addBudgetItem')}</span>
            </div>
          )}
        </div>

        <div className="budget-content">
          <div className="budget-table-container">
            <table className="budget-table">
              <thead>
                <tr>
                  <th className="checkbox-cell">
                    <div className="checkbox-wrapper">
                      <input 
                        type="checkbox"
                        checked={isAllSelected()}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </div>
                  </th>
                  <th className="expand-cell">
                    <div className="expand-wrapper">
                      <span 
                        className={`expand-icon ${isAllExpanded ? 'expanded' : ''}`}
                        onClick={handleToggleAll}
                      >
                        {renderExpandIcon(isAllExpanded)}
                      </span>
                    </div>
                  </th>
                  <th className="budgetItemCode-cell">{t('budget.budgetCode')}</th>
                  <th className="budgetItemName-cell">
                    <div className="budgetItemName-content">
                      <span>{t('budget.budgetItem')}</span>
                    </div>
                  </th>
                  <th className="budgetItemCurrency-cell">{t('budget.currency')}</th>
                  {/* <th className="budgetItemProgressType-cell">预算规则</th> */}
                  <th className="budgetItemUnitPrice-cell">{t('budget.unitPrice')}</th>
                  <th className="budgetItemQuantity1-cell">{t('budget.quantity1')}</th>
                  <th className="budgetItemUnit-cell">{t('budget.unit1')}</th>
                  <th className="budgetItemQuantity2-cell">{t('budget.quantity2')}</th>
                  <th className="budgetItemUnit2-cell">{t('budget.unit2')}</th>
                  <th className="budgetItemAmount-cell">{t('budget.subTotal')}</th>
                  <th className="budgetItemRelatedBudgetAmount-cell">{t('budget.relatedBudgetAmount')}</th>
                  {shouldShowAmountColumns() && (
                    <>
                      <th className="budgetItemUsedAmount-cell">{t('budget.usedAmount')}</th>
                      <th className="budgetItemRemainingAmount-cell">{t('budget.remainingAmount')}</th>
                    </>
                  )}
                  <th className="budgetItemRemark-cell">{t('budget.remark')}</th>
                  <th className="budgetItemAssignee-cell">{t('budget.assignee')}</th>
                </tr>
              </thead>
              <tbody>
                {getDisplayedBudgetItems().map(item => renderItem(item))}
              </tbody>
            </table>
          </div>
        </div>
      </div>




      <DeleteConfirm
        visible={deleteModalVisible}
        onConfirm={() => deleteItemId && handleDelete(deleteItemId)}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteItemId(null);
        }}
        title={deleteItemId ? findBudgetItemById(budgetItems, deleteItemId)?.budgetItemName || '' : ''}
        itemCode={deleteItemId ? findBudgetItemById(budgetItems, deleteItemId)?.budgetItemCode : undefined}
        itemName={deleteItemId ? findBudgetItemById(budgetItems, deleteItemId)?.budgetItemName : undefined}
      />

      <ProgressDialog
        visible={progressDialogVisible}
        message={progressDialogMessage}
        progress={progressDialogProgress}
      />
    </div>
  );
};

export default BudgetTable; 